import { Component, ElementRef, HostBinding, HostListener, Input, OnDestroy, OnInit, ViewChild, computed, effect, inject, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertifyService } from '../../../services/alertify.service';
import { AuthService } from '../../../services/auth.service';
import { parseUtcDate } from '../../../helpers/date.helper';
import { NEVER, Subject, Subscription, forkJoin, from, fromEvent, interval } from 'rxjs';
import { format } from 'date-fns';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { DEFAULT_COLOR_PALETTE, DEFAULT_TEXT_COLORS, DesignSaveFailure, END_CLIENT_REPORT_PROVIDER_LABELS, EndClientReport, EndClientReportStatus, EndClientReportTemplate, EndClientReportTheme, PageSection, ReportDesign, ReportStatistics, ReportSubSection, ReportType, ThemeChoice } from '../../../models/end-client-reporting-package';
import { v4 as uuidv4 } from 'uuid';
import {
  BlockDrop,
  BlockPatch,
  BlockWidth,
  PartVariable,
  addBlankSubSection,
  addBlock,
  applyThemePalette,
  withCustomLogo,
  withThemeLogo,
  withoutCustomLogo,
  addSection,
  canMoveBlockTo,
  templateDocumentFrom,
  dropBlock,
  moveBlock,
  moveBlockToSubSection,
  moveSection,
  moveSubSection,
  placeSubSection,
  removeBlock,
  removeSubSection,
  removeUnresolvedSlot,
  renameSubSection,
  renameSection,
  setBlockType,
  setBlockGroupBy,
  setBlockPriority,
  setBlockSubgroups,
  setBlockTableFilter,
  setReportStatistics,
  setBlockLayer,
  insertCrosstabAfter,
  crosstabKeys,
  locateBlockIn,
  setBlockVariable,
  setBlockVariables,
  setBlockWidth,
  ExplainerPlacement,
  explainerPlacement,
  setLadderExplainer,
  setSubSectionKeyTakeaway,
  setSubSectionSubheader,
  setPaletteColor,
  updateBlock,
} from '../report-design-edit';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ReportStatisticsModalComponent } from '../report-statistics-modal.component';
import { EndClientReportService } from '../end-client-report.service';
import { RefreshDataModalComponent } from '../refresh-data-modal.component';
import { DataSourceModalComponent } from '../data-source-modal.component';
import { ShareReportModalComponent } from '../share-report-modal.component';
import { ReportHistoryModalComponent } from '../report-history/report-history-modal.component';
import { REPORT_VIEW_MODE } from '../report-view-mode';
import type { ColorPalette, DesignChatViewing, ReportLogo } from '../../../models/end-client-reporting-package';
import { ReportSelectGroup, ReportSelectOption } from '../report-select.component';
import { explainerFromLadder } from '../report-blocks/explainer-from-ladder';
import { ladderMinWidth, rungLabels } from '../report-blocks/relationship-ladder-block.component';
import { explainerMinWidth } from '../report-blocks/explainer-panel-block.component';
import { REPORT_BLOCK_LABELS } from '../report-blocks/report-block-registry';
import { HeatmapImageStore } from '../report-blocks/heatmap-images';
import {
  ActiveFilters,
  AggregateTable,
  AnswerOption,
  ChartSeries,
  aggregate,
  PageRow,
  RespondentRecord,
  SurveyLayout,
  aggregateAll,
  applyFilters,
  filterOptions,
  ignoresCompareBy,
  layoutRows,
  pagesOf,
  pageOf,
  listVariables,
  studyFacts,
  optionsFor,
  seriesForBlock,
  toHex6,
  unresolvedVariables,
  weightedSize,
} from '../report-aggregation';
import { DeckWeighting, deckWeighting, weightsCsv } from '../weighting';
import { dataExport } from '../data-export/data-export';
import { dataDownloadsFor } from '../data-export/data-downloads';
import { DataDownloadModalComponent } from '../data-download-modal.component';
import { applyDeckTheme } from '../deck-theme';
import {
  CrosstabExplore, ExploreHandle, ExploreQuestion, exploredBlock, isPristine,
} from '../crosstab-explore';
import { WeightCandidate, resolveStatistics, weightCandidates } from '../crosstab-stats';

const REFRESH_POLL_MS = 5000;

/** The settings rail's width limits. Past the maximum the deck itself is squeezed out of reading. */
const RAIL_MIN = 280;
const RAIL_MAX = 640;
const RAIL_DEFAULT = 290;
const RAIL_WIDTH_KEY = 'ecr-edit-panel-width';

/** The report-colours popup's width (20rem in the stylesheet), for placing it under its button. */
const THEME_PANEL_WIDTH = 320;

/** EndClientReportHeatmapImages.MaxLogoBytes on the server (NK-518). */
const MAX_LOGO_BYTES = 2_000_000;

/** The stylesheet's narrow-window breakpoint, where the filter rail gives way to a tab in the index. */
const NARROW_LAYOUT_QUERY = '(max-width: 1200px)';

/** The width a person last left the rail at, or the default where nothing usable is stored. */
function readRailWidth(): number {
  try {
    const stored = Number(localStorage.getItem(RAIL_WIDTH_KEY));
    return Number.isFinite(stored) && stored >= RAIL_MIN && stored <= RAIL_MAX ? stored : RAIL_DEFAULT;
  } catch {
    return RAIL_DEFAULT;
  }
}



/**
 * How near a card's top or bottom edge a drag counts as aiming at the gap there rather than at the
 * card itself. A quarter of the card where it is shorter than four of these.
 */
const CARD_EDGE_PX = 48;

/** How long a confirmation banner stays up by itself. */
const CONFIRMATION_BANNER_MS = 8000;

/** How much of the chat window's bar is kept on screen, at the least, wherever it is dragged. */
const CHAT_BAR_REACH_PX = 44;

/** What a module's grip puts on a drag, so nothing but the deck reads it as something to take. */
const MODULE_DRAG_TYPE = 'application/x-navigator-module';
/** The same for a subsection's grip in the index. */
const SUBSECTION_DRAG_TYPE = 'application/x-navigator-subsection';

/**
 * A one-pixel transparent image, for a drag that should carry no picture at all -- without one, the
 * browser draws its own translucent copy of the card or the row. Made once, up front: an image still loading
 * when a drag starts is ignored, and the browser's copy comes back.
 */
const NO_DRAG_IMAGE = (() => {
  const image = new Image();
  image.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  return image;
})();

interface NavSubSection {
  sectionId: string;
  sectionName: string;
  subSection: ReportSubSection;
}

/** A card's padding either side (.report-block in report-block-host), which a column must hold too. */
const CARD_PADDING_X = 36;

/** Bootstrap's gutter inside a column, either side. */
const COLUMN_GUTTERS_X = 30;

interface RenderedBlock {
  block: PageSection;
  series: ChartSeries[];
  /** A crosstab a reader can drill into. */
  explore?: ExploreHandle;
}

interface RenderedRow {
  full?: RenderedBlock;
  left?: RenderedBlock;
  right?: RenderedBlock;
}

interface CompareColumn {
  label: string;
  size: number;
}

interface CompareRow {
  block: PageSection;
  alignCount: number;
  cells: { series: ChartSeries[]; size: number }[];
  /** Rendered once across every column instead of once per column. */
  spans: boolean;
}

interface FilterRow {
  key: string;
  options: AnswerOption[];
  /** The same options in the deck dropdown's shape. Answer text, so no code. */
  selectOptions: ReportSelectOption[];
}

@Component({
  selector: 'app-end-client-report-page',
  standalone: false,
  templateUrl: './end-client-report-page.component.html',
  styleUrls: ['./end-client-report-page.component.scss'],
  // One per deck: the heatmaps on it share their images, and the images go when the deck does.
  providers: [HeatmapImageStore],
})
export class EndClientReportPageComponent implements OnInit, OnDestroy {
  projectId = '';
  /** Set by the share-link viewer, which has no `reportId` in its route. Otherwise read from it. */
  @Input() reportId = '';

  /**
   * Drawn as a preview in the project's report list: the current page alone, with no top bar, index,
   * filter rail, banners or chat. The list pages it with `goToDeckPage`.
   */
  @Input() thumbnail = false;

  @HostBinding('class.is-thumbnail') get isThumbnail(): boolean {
    return this.thumbnail;
  }
  backLink = '';

  report = signal<EndClientReport | null>(null);
  design = signal<ReportDesign | null>(null);
  loading = signal(true);
  loadFailed = signal(false);

  presenting = signal(false);

  /**
   * Whether this is a Navigator user or someone who opened a share link (NK-399). From DI, and it
   * defaults to the client -- see REPORT_VIEW_MODE for why the safe state is the default.
   */
  readonly internal = inject(REPORT_VIEW_MODE) === 'internal';

  editing = signal(false);
  /** The report-colours section folded under the index's Theme button. Folded each time editing starts. */
  themePanelOpen = signal(false);
  editedName = signal('');
  editedDescription = signal('');
  nameSaveFailed = signal(false);

  dirty = signal(false);

  /**
   * The name and description are columns on the report row, not part of the design document, so
   * `dirty` -- which only `applyEdit` sets -- never saw them. Gated on `editing()` because the two
   * edited signals are only filled when edit mode opens; before that they are empty and would read
   * as a change from whatever the deck is called.
   */
  private nameDirty = computed(() => this.editing()
    && (this.editedName().trim() !== this.deckName()
      || this.editedDescription().trim() !== this.deckDescription()));

  /** Anything typed since editing began, wherever it is actually stored. */
  unsaved = computed(() => this.dirty() || this.nameDirty());
  savingDesign = signal(false);

  /**
   * Set when this route is editing a template rather than a deck. Everything about subsection and block
   * editing is identical; what differs is where the design comes from, where it goes, and the deck
   * chrome that has no meaning without a survey behind it — refresh, present, the AI chat.
   */
  templateId = '';
  template = signal<EndClientReportTemplate | null>(null);
  isTemplate = computed(() => !!this.templateId);

  /** Name, description and save version, from whichever of the two this route is editing. */
  deckName = computed(() => (this.isTemplate() ? this.template()?.name : this.report()?.name) ?? '');
  deckDescription = computed(() =>
    (this.isTemplate() ? this.template()?.description : this.report()?.description) ?? '');
  baseVersion = computed(() =>
    (this.isTemplate() ? this.template()?.templateVersion : this.report()?.reportDesignVersion) ?? 0);

  /**
   * NK-450 — the client's themes for the edit-mode picker, OR Default first. Fetched the first time
   * edit mode opens rather than on load: a deck that's only being read never needs them.
   */
  themes = signal<EndClientReportTheme[]>([]);
  private themesLoaded = false;

  /**
   * A theme picked during this edit, not yet saved. Sent with the design save so the theme columns
   * and the colours land together; null means the edit hasn't touched the theme, which is also what
   * the save then says.
   */
  private pendingTheme = signal<ThemeChoice | null>(null);

  /** The theme the deck is on as the picker should show it: the pending pick, else the stored one. */
  currentThemeId = computed<string | null>(() => {
    const pending = this.pendingTheme();
    if (pending) return pending.themeId;
    return (this.isTemplate() ? this.template()?.themeId : this.report()?.themeId) ?? null;
  });

  /** The theme the deck is on, at its latest colours. Null until themes load, or where it was deleted. */
  currentTheme = computed<EndClientReportTheme | null>(() =>
    this.themes().find(theme => theme.id === this.currentThemeId()) ?? null);

  /**
   * Whether the deck's colours have been changed by hand since its theme was applied, which only
   * ever changes this deck -- a theme is copied in, never linked. Read off the colours themselves
   * rather than stored, so changing a colour back counts as back.
   *
   * Not while the theme is out of date: the deck then still matches an older version of it, and the
   * picker already offers that update -- calling it custom would be wrong about why it differs.
   */
  coloursCustomised = computed(() => {
    const theme = this.currentTheme();
    if (!theme || this.themeOutOfDate()) return false;

    const ours = this.palette();
    const theirs = theme.theme.colorPalette;
    const same = (a: string | undefined, b: string | undefined) =>
      (toHex6(a ?? '') ?? a) === (toHex6(b ?? '') ?? b);
    const roles: (keyof ColorPalette)[] = ['primary', 'secondary', 'accent', 'positive', 'negative'];

    return roles.some(role => !same(ours[role], theirs[role]))
      || !same(ours.darkText ?? DEFAULT_TEXT_COLORS.darkText, theirs.darkText ?? DEFAULT_TEXT_COLORS.darkText)
      || !same(ours.lightText ?? DEFAULT_TEXT_COLORS.lightText, theirs.lightText ?? DEFAULT_TEXT_COLORS.lightText);
  });

  /** A pick made during this edit applied the latest colours, so only the stored state can be behind. */
  themeOutOfDate = computed(() => !this.pendingTheme()
    && !!(this.isTemplate() ? this.template()?.themeOutOfDate : this.report()?.themeOutOfDate));

  savingTemplate = signal(false);
  /** The template's name once one has been saved from this deck, for the confirmation banner. */
  templateSaved = signal<string | null>(null);
  templateSaveFailed = signal(false);

  /**
   * The AI chat's window shrunk to a pill -- which is how it starts. Its conversation is kept: only
   * the window is hidden. The chat itself is always there wherever it can be used (see
   * chatAvailable); this is the only thing about it that opens and closes.
   */
  chatMinimized = signal(true);
  /** Where the chat window has been dragged to, in the viewport. Null until it is: the bottom right. */
  chatPosition = signal<{ left: number; top: number } | null>(null);
  /** Where in the window's bar the pointer took hold, while it is being dragged. */
  private chatDrag: { dx: number; dy: number } | null = null;
  /** The module whose settings are open in the side panel, if any. Edit mode only. */
  editedBlockId = signal<string | null>(null);

  /**
   * The module being dragged by its grip. Two ids, because they differ on an explainer paired with a
   * matrix: dragged about its own page, the explainer moves; dropped on another subsection, its
   * matrix does, and takes the explainer along.
   */
  dragging = signal<{ blockId: string; moveTarget: string } | null>(null);
  /** Where on the page a drop would put the dragged card, as dropBlock takes it. Null for nowhere new. */
  drop = signal<BlockDrop | null>(null);
  /** The subsection in the index the drag is over, where it can be dropped there. */
  dropSubSectionId = signal<string | null>(null);
  /** The subsection being dragged by its grip in the index. */
  subSectionDrag = signal<string | null>(null);
  /** Where in the index letting go would put it: in front of a subsection, or at the end of a section. */
  subSectionDrop = signal<{ sectionId: string; beforeId: string | null } | null>(null);
  /** The last module moved to another subsection, for the panel's "Go there". */
  movedAway = signal<{ blockId: string; subSectionId: string; label: string; subSectionName: string } | null>(null);
  /** Said to a screen reader after a move: the card moved out from under the focus, or off the page. */
  moveAnnouncement = signal('');
  confirmingSubSectionDelete = signal(false);
  /** Cancel asks once before throwing work away, the way removing a subsection does. */
  confirmingCancel = signal(false);

  /**
   * The design as it stood when edit mode was entered. Nothing here writes to the server until Done
   * editing, so putting this back *is* the revert -- and because every edit returns a new design
   * rather than mutating one, holding the old reference is a complete snapshot.
   */
  private designBeforeEditing: ReportDesign | null = null;
  // The chat edit saved but this view couldn't re-read the design afterwards, so what's on screen
  // is behind what's stored. Distinct from a failed save, which is the chat panel's own to report.
  staleAfterEdit = signal(false);
  designSaveFailure = signal<DesignSaveFailure | null>(null);

  private records = signal<RespondentRecord[]>([]);
  private layout = signal<SurveyLayout>({});

  selectedSubSectionId = signal<string | null>(null);
  activeFilters = signal<ActiveFilters>({});
  compareBy = signal('');

  navSubSections = computed<NavSubSection[]>(() => {
    const design = this.design();
    if (!design) return [];
    return design.sectionOrder.flatMap(sectionId => {
      const section = design.sections[sectionId];
      if (!section) return [];
      return section.pageOrder
        .map(subSectionId => section.pages[subSectionId])
        .filter((subSection): subSection is ReportSubSection => !!subSection)
        .map(subSection => ({ sectionId, sectionName: section.name, subSection }));
    });
  });

  /** What the nav's search box holds. Narrows the subsection list only -- never the deck itself. */
  navQuery = signal('');

  /**
   * A window too narrow for the index, the deck and the filter rail side by side. The filters move
   * into a second tab of the index instead of disappearing, so they can still be reached.
   */
  narrowLayout = signal(window.matchMedia(NARROW_LAYOUT_QUERY).matches);
  filtersInNav = computed(() => this.narrowLayout() && !this.editing());

  /** Which of the index's tabs is showing while it holds the filters too. */
  navTab = signal<'sections' | 'filters'>('sections');

  /**
   * The index folded down to a strip, giving the deck its width on a narrow window. Remembered while
   * the window is wide, but only applied while it is narrow: there is room for the index there.
   */
  private navCollapsedChoice = signal(false);
  navCollapsed = computed(() => this.narrowLayout() && this.navCollapsedChoice());

  /** Folds or unfolds the index, and moves focus to the button that undoes it. */
  setNavCollapsed(collapsed: boolean) {
    this.navCollapsedChoice.set(collapsed);
    setTimeout(() => document.getElementById(collapsed ? 'deck-nav-expand' : 'deck-nav-collapse')?.focus());
  }

  /**
   * The subsection list as the nav draws it. Separate from `navSubSections()` on purpose: that stays the whole
   * deck, so the subsection counter, the present-mode arrows and "which subsection am I on" keep counting every
   * subsection whatever is typed here. Section names match too, so searching a section's name brings back
   * its subsections rather than nothing.
   */
  visibleNavSubSections = computed<NavSubSection[]>(() => {
    const query = this.navQuery().trim().toLowerCase();
    if (!query) return this.navSubSections();
    return this.navSubSections().filter(nav =>
      nav.subSection.name.toLowerCase().includes(query) || nav.sectionName.toLowerCase().includes(query));
  });

  subSectionIndex = computed(() => this.navSubSections().findIndex(nav => nav.subSection.id === this.selectedSubSectionId()));
  subSectionCount = computed(() => this.navSubSections().length);

  selectedSubSection = computed<ReportSubSection | null>(() => this.navSubSections()[this.subSectionIndex()]?.subSection ?? null);

  filterRows = computed<FilterRow[]>(() => {
    const design = this.design();
    if (!design) return [];
    return design.filters.filterableValues.filter(key => !this.isNumeric(key)).map(key => {
      const options = filterOptions(this.records(), key, design.variableMap, this.layout());
      return {
        key,
        options,
        // Built here, not in the template: or-report-select rebuilds on every input change, and a
        // getter would hand it a new array on every change-detection pass. Stringified because an
        // answer value can be a number, which <option> used to coerce for us -- and setFilter and
        // activeFilters have always dealt in the string the DOM handed back.
        selectOptions: options.map(option => ({ value: String(option.value), text: option.label })),
      };
    });
  });

  /**
   * Where the open module can go, grouped under the sections, for the panel's Subsection setting --
   * the way to move one without dragging. Its own subsection is listed too, as the current value;
   * any it cannot go to (see canMoveBlockTo) are left out rather than listed to be refused.
   */
  moveTargetGroups = computed<ReportSelectGroup[]>(() => {
    const design = this.design();
    const blockId = this.editedBlock()?.block.id;
    const current = this.selectedSubSectionId();
    if (!design || !blockId) return [];

    const groups = new Map<string, ReportSelectGroup>();
    for (const nav of this.navSubSections()) {
      const id = nav.subSection.id;
      if (id !== current && !canMoveBlockTo(design, blockId, id)) continue;
      const group = groups.get(nav.sectionId) ?? { label: nav.sectionName, options: [] };
      group.options.push({ value: id, text: nav.subSection.name });
      groups.set(nav.sectionId, group);
    }
    return [...groups.values()];
  });

  /**
   * Compare-by choices. Text only, no data position: the rail is narrow and this is a short list of
   * questions someone already recognises, so the code read as noise rather than the disambiguator
   * it is in the editor's whole-survey pickers.
   */
  compareByOptions = computed<ReportSelectOption[]>(() => {
    const design = this.design();
    if (!design) return [];
    return design.filters.compareBy
      .filter(key => !this.isNumeric(key))
      .map(key => ({ value: key, text: this.variableName(key) }));
  });

  /**
   * A numeric battery has no answer options, so it can neither filter nor split respondents -- every
   * one of them has their own score. Nothing stops a generated design listing one here, and an
   * empty rail row or a compare-by that quietly does nothing is worse than not offering it.
   */
  private isNumeric(key: string): boolean {
    return this.pickableVariables().find(variable => variable.key === key)?.kind === 'numeric';
  }

  private filteredRecords = computed(() => {
    const design = this.design();
    return design
      ? applyFilters(this.records(), this.activeFilters(), design.variableMap, this.layout())
      : this.records();
  });

  /**
   * Always a palette, never undefined: the deck's whole chrome is painted from it, and a deck that
   * renders uncoloured for the moment before its design arrives flashes grey and then repaints.
   * The template's existing `palette()?.x` reads are unaffected.
   */
  palette = computed<ColorPalette>(() => this.design()?.colorPalette ?? DEFAULT_COLOR_PALETTE);

  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * The deck's theme scope. On the host rather than on `.deck` inside the template because the
   * effect below writes its tokens here: a custom property declared by the `.ecr-theme` rule on a
   * *descendant* would shadow the inherited runtime value, and the fixed neutrals in the stylesheet
   * would quietly win over the derived ones. Same element for both, and the inline style wins.
   */
  @HostBinding('class.ecr-theme') readonly ecrTheme = true;

  /** The filters and compare-by set aside on the way into edit mode, to be put back on the way out. */
  private viewBeforeEditing: { filters: ActiveFilters; compareBy: string } | null = null;

  /**
   * NK-450 — colours to draw the deck in instead of its own, for the theme editor's preview. Written
   * into the loaded design rather than layered over `palette()`, because the blocks are handed
   * `design.colorPalette` directly; the preview never saves, so the design it changes is a throwaway.
   */
  @Input() set paletteOverride(value: ColorPalette | null | undefined) {
    this.overridePalette.set(value ?? null);
  }
  private overridePalette = signal<ColorPalette | null>(null);

  /**
   * NK-518 — the logo the theme editor is working on, drawn in place of the deck's own. Undefined
   * leaves the deck's; null means the theme being edited has none, so neither does the preview.
   */
  @Input() set logoOverride(value: ReportLogo | null | undefined) {
    this.overrideLogo.set(value);
  }
  private overrideLogo = signal<ReportLogo | null | undefined>(undefined);

  /** The logo the top bar draws, by image id: the deck's, unless it hides it. Null for none. */
  logoId = computed<string | null>(() => {
    const design = this.design();
    if (!design || design.hideLogo) return null;
    const override = this.overrideLogo();
    return (override === undefined ? design.logo : override)?.imageId ?? null;
  });

  /** The logo as something an <img> can show. Fetched rather than linked: the endpoint needs the token. */
  logoUrl = signal<string | null>(null);
  private logoLoad?: Subscription;

  private readonly loadLogo = effect(() => {
    const imageId = this.logoId();
    untracked(() => {
      this.logoLoad?.unsubscribe();
      if (!imageId) {
        this.showLogo(null);
        return;
      }
      this.logoLoad = this.reportService.getLogo(imageId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(blob => this.showLogo(blob ? URL.createObjectURL(blob) : null));
    });
  });

  private showLogo(url: string | null) {
    const previous = this.logoUrl();
    if (previous) URL.revokeObjectURL(previous);
    this.logoUrl.set(url);
  }

  // After loading as well as on every change, so the first paint is already in the edited colours.
  private readonly applyPaletteOverride = effect(() => {
    const override = this.overridePalette();
    if (!override || this.loading()) return;
    untracked(() => this.design.update(design => design && { ...design, colorPalette: { ...override } }));
  });

  /**
   * Into edit mode, the index's search is cleared: Add section takes the search box's place there,
   * and an index left narrowed would have nothing to widen it again. The filters and compare-by are
   * set aside too: edit mode has no filter rail to show or change them, and a deck edited through a
   * filter or split into columns is not the deck being designed -- a card edited while half its
   * respondents were filtered out looks emptier than it is.
   *
   * Out of it, whichever way -- Done, Cancel, a save that lands, a revert -- they are put back as they
   * were, and the module that was open goes. The card's ring is drawn from `editedBlockId`, not from
   * edit mode, so without that the last card edited stayed outlined in the deck as it is read.
   */
  private readonly onEditModeChange = effect(() => {
    const editing = this.editing();
    untracked(() => {
      if (editing) {
        this.navQuery.set('');
        this.viewBeforeEditing = { filters: this.activeFilters(), compareBy: this.compareBy() };
        // A drilled-in table is a reading of the deck, not part of it; the editor shows what is saved.
        this.exploreStates.set({});
        this.activeFilters.set({});
        this.compareBy.set('');
        return;
      }
      this.editedBlockId.set(null);
      if (this.viewBeforeEditing) {
        this.activeFilters.set(this.viewBeforeEditing.filters);
        this.compareBy.set(this.viewBeforeEditing.compareBy);
        this.viewBeforeEditing = null;
      }
    });
  });

  /**
   * Publishes the report's palette to CSS as `--ecr-*` custom properties on the deck element, so
   * the shell, the chrome and the module components all repaint together when someone changes a
   * colour. Same mechanism `ReportBlockHostComponent` uses for `--highcharts-color-*`, one level
   * up: custom properties inherit through view encapsulation, so a module's inline styles can read
   * these without the palette having to be threaded through as an input.
   *
   * The tokens themselves come from `deckThemeTokens`, which the module guide paints with too.
   */
  private readonly themeTokens = effect(() => applyDeckTheme(this.host.nativeElement, this.palette()));

  /**
   * Deliberately over records(), not filteredRecords(): a methodology module describes the study as
   * fielded, so it has to read the same whoever the deck is currently filtered to.
   */
  facts = computed(() => studyFacts(this.records(), this.report() ?? undefined));

  /**
   * Every question in the survey, mapped ones first. Computed here rather than inside each block
   * editor: it walks the whole datamap, and a subsection can hold several charts.
   */
  pickableVariables = computed(() => {
    const design = this.design();
    return design ? listVariables(design, this.layout()) : [];
  });

  baseN = computed(() => this.filteredRecords().length);
  totalN = computed(() => this.records().length);
  hasActiveFilters = computed(() => Object.values(this.activeFilters()).some(value => !!value));

  activeChips = computed<{ key: string; label: string }[]>(() => {
    const design = this.design();
    if (!design) return [];
    return Object.entries(this.activeFilters())
      .filter(([, code]) => !!code)
      .map(([key, code]) => {
        const option = optionsFor(key, design.variableMap, this.layout())
          .find(o => String(o.value) === code);
        return { key, label: option?.label ?? code };
      });
  });

  /**
   * Each respondent's weight, and how good the weighting is. Worked out over every respondent rather
   * than the filtered set: a filter shows a subgroup's weighted figures, it does not re-weight it.
   */
  weighting = computed<DeckWeighting>(() => {
    const design = this.design();
    if (!design) return { diagnostics: null };
    return deckWeighting(this.records(), design.statistics, design.variableMap, this.layout());
  });

  private weightOf = computed(() => this.weighting().weightOf);

  /** Columns in the data that look like respondent weights, for the Statistics settings. */
  weightColumns = computed<WeightCandidate[]>(() => {
    const layout = this.layout();
    const known = new Set<string>(['qtime']);
    for (const [position, variable] of Object.entries(layout)) {
      known.add(position);
      if ('column' in variable) known.add(variable.column);
      if ('columns' in variable) variable.columns.forEach(column => known.add(column.column));
    }
    return weightCandidates(this.records(), known);
  });

  private aggregates = computed<AggregateTable>(() => {
    const design = this.design();
    if (!design) return {};
    return aggregateAll(this.filteredRecords(), Object.keys(design.variableMap), design.variableMap, this.layout(),
      this.weightOf());
  });

  // The same table over every respondent, ignoring the rail. Only categorical-distribution blocks
  // read it, but it is computed once here rather than per block, and it depends on records/design
  // rather than activeFilters, so it does not recompute on a filter change.
  private overallAggregates = computed<AggregateTable>(() => {
    const design = this.design();
    if (!design) return {};
    return aggregateAll(this.records(), Object.keys(design.variableMap), design.variableMap, this.layout(),
      this.weightOf());
  });

  /**
   * Which page of the current subsection is showing. Held as the page's own number rather than an
   * index into pages(), so it survives the page ceasing to exist -- page() falls back to the first
   * rather than pointing at a slot that has moved under it.
   */
  private wantedPage = signal(0);

  /** The pages this subsection has, in order. [0] for one that has never used them. */
  pages = computed<number[]>(() => {
    const subSection = this.selectedSubSection();
    return subSection ? pagesOf(subSection) : [0];
  });

  /** The page actually shown: what was asked for, or the first if that page is no longer there. */
  page = computed<number>(() => {
    const pages = this.pages();
    const wanted = this.wantedPage();
    return pages.includes(wanted) ? wanted : pages[0];
  });

  pageIndex = computed(() => Math.max(0, this.pages().indexOf(this.page())));
  pageCount = computed(() => this.pages().length);

  /** What the AI chat is told is on screen, so "this page" means the one being looked at. */
  chatViewing = computed<DesignChatViewing | null>(() => {
    const nav = this.navSubSections()[this.subSectionIndex()];
    return nav ? { sectionId: nav.sectionId, subSectionId: nav.subSection.id, page: this.page() } : null;
  });

  /** Every page of the deck in reading order, across subsections, as the list's carousel counts them. */
  deckPages = computed<{ subSectionId: string; page: number }[]>(() =>
    this.navSubSections().flatMap(nav =>
      pagesOf(nav.subSection).map(page => ({ subSectionId: nav.subSection.id, page }))));

  deckPageIndex = computed(() => Math.max(0, this.deckPages().findIndex(entry =>
    entry.subSectionId === this.selectedSubSectionId() && entry.page === this.page())));

  /** The subsection the current page is in, so a thumbnail can say what it is showing. */
  currentSubSectionName = computed(() => this.selectedSubSection()?.name ?? '');

  /** Out-of-range indexes are clamped, so a stale `?page=` from a link still opens somewhere. */
  goToDeckPage(index: number): void {
    const pages = this.deckPages();
    if (!pages.length) return;
    const target = pages[Math.min(Math.max(0, index), pages.length - 1)];
    this.selectedSubSectionId.set(target.subSectionId);
    this.wantedPage.set(target.page);
  }

  renderedRows = computed<RenderedRow[]>(() => {
    const page = this.selectedSubSection();
    const design = this.design();
    if (!page || !design) return [];
    const aggregates = this.aggregates();
    const overall = this.overallAggregates();
    // `records` is the rail-filtered set: a small-multiples block aggregates its own groups from it,
    // stacking each group on top of whatever the rail is already filtering.
    const context = {
      variableMap: design.variableMap,
      layout: this.layout(),
      overallAggregates: overall,
      records: this.filteredRecords(),
      weightOf: this.weightOf(),
      statistics: design.statistics,
    };
    const resolve = (block?: PageSection): RenderedBlock | undefined => {
      if (!block) return undefined;
      // The explainer's stages come from what its ladder *draws*, which needs that ladder's own
      // series -- the rungs are the answers in the data, not the names written on the block.
      const ladder = block.reportType === 'stage-explainer' ? this.ladderIn(page) : undefined;
      const rungs = ladder
        ? rungLabels(seriesForBlock(ladder, aggregates, context))
        : [];
      const drawn = this.asDrawn(block, ladder, rungs);
      if (drawn.reportType === 'crosstab' && this.explorable()) {
        const state = this.exploreStates()[drawn.id];
        const explored = exploredBlock(drawn, state);
        // A reader can bring in a question the deck never charted, which `variableMap` cannot
        // resolve until it is mapped -- so it is resolved here, through its position, for this view.
        const variableMap = { ...design.variableMap, ...this.positionsFor(crosstabKeys(explored)) };
        return {
          block: explored,
          series: seriesForBlock(explored, aggregates, { ...context, variableMap }),
          explore: this.exploreHandle(drawn.id, state),
        };
      }
      return { block: drawn, series: seriesForBlock(drawn, aggregates, context) };
    };

    return layoutRows(page, this.page()).map((row: PageRow) => ({
      full: resolve(row.full),
      left: resolve(row.left),
      right: resolve(row.right),
    }));
  });

  /**
   * The answers a question offers, for the editor's subgroup picker. A bound arrow rather than a
   * method so the template can hand the function itself to a child, and read off the unfiltered
   * records: a panel definition belongs to the design and must not change with the filter rail.
   */
  answersOf = (variableKey: string): string[] => {
    const design = this.design();
    if (!design || !variableKey) return [];

    // A question the deck has never charted is not in `variableMap`, and an unmapped key resolves
    // to nothing -- no answers, so the editor offers an empty tick list for a question it has just
    // listed. Its data position is known all the same (`listVariables` carries one for every
    // question in the survey), so resolve through that. Nothing is written here: the position
    // reaches `variableMap` when the choice is saved, and this is only what makes the choice
    // possible to make.
    const map = variableKey in design.variableMap
      ? design.variableMap
      : {
          ...design.variableMap,
          ...this.positionOf(variableKey),
        };

    return filterOptions(this.records(), variableKey, map, this.layout())
      .map(option => option.label);
  };

  /**
   * Each answer's unweighted share of a question, for the weighting targets beside them. Over every
   * respondent, since weights are set on the whole sample. Resolved through the question's position
   * when it is not yet mapped, the same way `answersOf` is.
   */
  sharesOf = (variableKey: string): { answer: string; percent: number }[] => {
    const design = this.design();
    if (!design || !variableKey) return [];
    const map = variableKey in design.variableMap
      ? design.variableMap
      : { ...design.variableMap, ...this.positionOf(variableKey) };
    return aggregate(this.records(), variableKey, map, this.layout()).points
      .filter(point => point.optionIndex >= 0)
      .map(point => ({ answer: point.label, percent: point.percent }));
  };

  /** One row per respondent and their weight, for a DP team to check or reuse. */
  downloadWeights() {
    const weightOf = this.weightOf();
    const records = this.records();
    if (!weightOf || !records.length) return;
    const idColumn = ['record', 'uuid'].find(column => column in records[0]) ?? '';
    const blob = new Blob([weightsCsv(records, weightOf, idColumn)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.deckName() || 'report'} weights.csv`.replace(/[^\w\- .]+/g, '');
    link.click();
    URL.revokeObjectURL(url);
  }

  /** The source's provider's own file shapes. None where the source has no provider recorded. */
  dataDownloads = computed(() => dataDownloadsFor(this.report()?.dataSourceProvider));

  noDownloadReason = computed(() => {
    const provider = this.report()?.dataSourceProvider;
    return provider === null || provider === undefined
      ? 'This data source has no provider recorded'
      : `Not set up for ${END_CLIENT_REPORT_PROVIDER_LABELS[provider]} data yet`;
  });

  hasRecords = computed(() => this.records().length > 0);

  /**
   * Every respondent in the source, with the weights the deck shows -- never just the filtered few,
   * since the weights are worked out on the whole sample. The dialog picks the file; this makes it.
   */
  openDataDownload() {
    const downloads = this.dataDownloads();
    if (this.isTemplate() || !downloads.length) return;
    const modal = this.modalService.show(DataDownloadModalComponent, { class: 'modal-md' });
    const dialog = modal.content;
    if (!dialog) return;
    dialog.offer(downloads, this.report()?.dataSourceName ?? '', !!this.weightOf());
    // Closing the dialog while the file is being made gives up on it.
    const closed$ = modal.onHidden ?? NEVER;
    dialog.onDownload = download => {
      const records = this.records();
      const weightOf = this.weightOf();
      this.reportService.getDatamap(this.reportId).pipe(
        switchMap(datamap => from(download.build(dataExport(datamap, records, weightOf), this.deckName()))),
        takeUntil(this.destroy$),
        takeUntil(closed$),
      ).subscribe({
        next: ({ file, fileName }) => {
          const url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(url);
          dialog.downloaded();
        },
        error: () => dialog.downloadFailed(),
      });
    };
  }

  /**
   * Each reader's drill into each crosstab, by block. The reader's own and never saved: the design is
   * the published table, and this is what one person has done to it since opening the deck.
   */
  private exploreStates = signal<Record<string, CrosstabExplore>>({});

  /**
   * A drill's breadcrumb records how many respondents each step kept. Change the deck's filters
   * underneath it and those counts are of a different sample -- so a drill is put away rather than
   * left standing over figures it no longer describes.
   */
  private readonly resetExploreOnFilters = effect(() => {
    this.activeFilters();
    this.compareBy();
    untracked(() => this.exploreStates.set({}));
  });

  /** Only on the deck as read: an edited deck is the published table, and a thumbnail is a picture. */
  private explorable = computed(() => !this.thumbnail && !this.editing());

  /** Questions a reader can put on a table's axes or slice it by. */
  private exploreQuestions = computed<ExploreQuestion[]>(() => this.pickableVariables()
    .filter(variable => !variable.partOf && (variable.kind === 'coded' || variable.kind === 'multi'
      || variable.kind === 'numeric'))
    .map(variable => ({
      key: variable.key,
      label: variable.label,
      code: variable.position,
      splits: variable.kind !== 'numeric',
    })));

  private exploreHandle(blockId: string, state: CrosstabExplore | undefined): ExploreHandle {
    return {
      state,
      questions: this.exploreQuestions(),
      change: next => this.exploreStates.update(all => {
        const copy = { ...all };
        if (next && !isPristine(next)) copy[blockId] = next;
        else delete copy[blockId];
        return copy;
      }),
      saveAsTable: this.internal && !this.isTemplate() ? () => this.saveExploredTable(blockId) : undefined,
    };
  }

  /**
   * Keeps a reader's drilled-in view as a table of its own, straight under the one it came from,
   * and opens it in the editor -- the deck goes into edit mode, since the new table is an edit to
   * save like any other.
   */
  private saveExploredTable(blockId: string) {
    const design = this.design();
    const state = this.exploreStates()[blockId];
    const source = design && locateBlockIn(design, blockId);
    if (!design || !source || source.reportType !== 'crosstab' || !state) return;

    const explored = exploredBlock(source, state);
    const path = state.path.map(step => step.answer).join(' › ');
    const id = uuidv4();
    const block = {
      ...explored,
      id,
      header: [source.header || 'Crosstab', path].filter(Boolean).join(' — '),
    };
    const variables = crosstabKeys(explored).map(key => ({ key, position: this.positionOf(key)[key] }));

    if (!this.editing()) this.toggleEditing();
    this.applyEdit(current => insertCrosstabAfter(current, blockId, block, variables));
    this.exploreStates.update(all => {
      const copy = { ...all };
      delete copy[blockId];
      return copy;
    });
    this.openBlockEditor(id);
  }

  /**
   * The same table again for each question, one under another beneath the original: how a report
   * full of tables is built from one set up by hand. Each copy keeps the columns, tests, split and
   * display; nets and the mean are dropped, being written in the original question's answers.
   */
  copyCrosstab(blockId: string, variables: PartVariable[]) {
    const design = this.design();
    const source = design && locateBlockIn(design, blockId);
    if (!source || source.reportType !== 'crosstab' || !variables.length) return;

    let after = blockId;
    this.applyEdit(current => variables.reduce((next, variable) => {
      const id = uuidv4();
      const copy = {
        ...source,
        id,
        header: this.pickableVariables().find(candidate => candidate.key === variable.key)?.label ?? variable.key,
        reportExtraData: {
          ...source.reportExtraData,
          variableKey: variable.key,
          nets: [],
          showMean: false,
          meanExcludes: [],
        },
      };
      const placed = insertCrosstabAfter(next, after, copy, [variable]);
      after = id;
      return placed;
    }, current));
  }

  /** Positions for keys `variableMap` does not have yet, so an explored view resolves. */
  private positionsFor(keys: string[]): Record<string, string> {
    const map = this.design()?.variableMap ?? {};
    return Object.assign({}, ...keys.filter(key => !(key in map)).map(key => this.positionOf(key)));
  }

  readonly railMin = RAIL_MIN;
  readonly railMax = RAIL_MAX;
  readonly railDefault = RAIL_DEFAULT;

  /** The settings rail's width, as the person last left it on this browser. */
  editPanelWidth = signal(readRailWidth());
  resizingRail = signal(false);

  setRailWidth(width: number) {
    const clamped = Math.round(Math.min(RAIL_MAX, Math.max(RAIL_MIN, width)));
    this.editPanelWidth.set(clamped);
    try {
      localStorage.setItem(RAIL_WIDTH_KEY, String(clamped));
    } catch {
      // Storage refused -- private mode, a full quota. The width still holds for this visit.
    }
  }

  /**
   * Dragging the rail's edge. Pointer capture keeps the move events coming however fast the pointer
   * goes, and the rail grows leftward -- the deck gives up the room.
   */
  startRailResize(event: PointerEvent) {
    if (event.button !== 0) return;
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    const startX = event.clientX;
    const startWidth = this.editPanelWidth();
    handle.setPointerCapture(event.pointerId);
    this.resizingRail.set(true);

    const move = (e: PointerEvent) => this.editPanelWidth.set(
      Math.round(Math.min(RAIL_MAX, Math.max(RAIL_MIN, startWidth + (startX - e.clientX)))));
    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      this.resizingRail.set(false);
      this.setRailWidth(this.editPanelWidth());
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  /** The sidebar Statistics button's summary: weighting and confidence, the two most often changed. */
  statisticsSummary = computed(() => {
    const resolved = resolveStatistics(this.design()?.statistics);
    const weighting = resolved.weighting?.method === 'column' ? `Weighted by ${resolved.weighting.column}`
      : resolved.weighting ? 'Weighted to targets' : 'Unweighted';
    return `${weighting} · ${resolved.confidenceLevel}%`;
  });

  /**
   * A Theme or Statistics change made while viewing, held until the panel's own Save or Cancel.
   * The deck previews it as it is made -- the change goes into `design` like any edit -- and `before`
   * is what Cancel puts back. Null while editing, where the edit's draft and Save changes cover it.
   */
  private settingsSession = signal<{ before: ReportDesign } | null>(null);
  previewingSettings = computed(() => this.settingsSession() !== null);

  /** Starts a session if viewing. False while editing, or with no design to change. */
  private beginSettingsSession(): boolean {
    if (this.editing() || this.savingDesign()) return false;
    const design = this.design();
    if (!design) return false;
    if (!this.settingsSession()) {
      this.settingsSession.set({ before: design });
      this.pendingTheme.set(null);
      this.designSaveFailure.set(null);
    }
    return true;
  }

  /** The panel's Save: writes the design as a normal saved version, then runs `after`. */
  saveSettings(after?: () => void) {
    if (!this.settingsSession() || this.savingDesign()) return;
    if (!this.dirty()) {
      this.settingsSession.set(null);
      after?.();
      return;
    }
    this.saveDesign(() => {
      this.settingsSession.set(null);
      after?.();
    }, false);
  }

  /** The panel's Cancel, its ×, Esc or a click away: the deck goes back to how it was saved. */
  discardSettings() {
    const session = this.settingsSession();
    if (!session || this.savingDesign()) return;
    this.design.set(session.before);
    this.dirty.set(false);
    this.pendingTheme.set(null);
    this.designSaveFailure.set(null);
    this.settingsSession.set(null);
  }

  /**
   * The report's statistics, in a dialog over the deck. Opened from the top bar and from a crosstab's
   * settings; the module being edited stays open behind it. While editing, changes go into the
   * edit's draft. While viewing, the dialog carries its own Save and Cancel, and closing it any other
   * way cancels. It reads the deck's signals rather than a copy, so the diagnostics and achieved
   * targets move as changes are applied.
   */
  showStatistics() {
    const standalone = !this.editing();
    if (standalone && !this.beginSettingsSession()) return;
    this.themePanelOpen.set(false);
    const initialState: Partial<ReportStatisticsModalComponent> = {
      statistics: () => this.design()?.statistics,
      diagnostics: () => this.weighting().diagnostics,
      weightColumns: () => this.weightColumns(),
      variables: () => this.pickableVariables(),
      sharesOf: this.sharesOf,
      onChange: change => this.setStatistics(change),
      onDownloadWeights: () => this.downloadWeights(),
      standalone,
      changed: () => this.dirty(),
      saving: () => this.savingDesign(),
      failed: () => this.designSaveFailure() !== null,
      onSave: () => this.saveSettings(() => modal.hide()),
    };
    const modal = this.modalService.show(ReportStatisticsModalComponent, {
      initialState,
      // ecr-stats-dialog: the whole screen on a phone (themes/end-client-reporting.scss).
      class: 'modal-lg modal-dialog-centered ecr-stats-dialog',
      ariaLabelledBy: 'report-statistics-title',
    });
    // However a standalone dialog closes without saving, its changes are put back.
    if (standalone) modal.onHidden?.pipe(take(1)).subscribe(() => this.discardSettings());
  }

  /** A question's answers with their codes -- what tells a scale from its "Don't know". */
  answerOptionsOf = (variableKey: string): AnswerOption[] | null => {
    const design = this.design();
    if (!design || !variableKey) return null;
    const map = variableKey in design.variableMap
      ? design.variableMap
      : { ...design.variableMap, ...this.positionOf(variableKey) };
    const options = optionsFor(variableKey, map, this.layout());
    return options.length ? options : null;
  };

  /** One entry, or none at all when the key is not a question in this survey. */
  private positionOf(variableKey: string): Record<string, string> {
    const position = this.pickableVariables().find(v => v.key === variableKey)?.position;
    return position ? { [variableKey]: position } : {};
  }

  compareOptions = computed<AnswerOption[]>(() => {
    const design = this.design();
    const key = this.compareBy();
    if (!design || !key) return [];
    return filterOptions(this.filteredRecords(), key, design.variableMap, this.layout());
  });

  comparing = computed(() => !!this.compareBy() && this.compareOptions().length > 0);

  private compareData = computed<{ columns: CompareColumn[]; rows: CompareRow[] }>(() => {
    const page = this.selectedSubSection();
    const design = this.design();
    const key = this.compareBy();
    if (!page || !design || !this.comparing()) return { columns: [], rows: [] };

    const variableKeys = Object.keys(design.variableMap);
    const blocks = page.pageSectionOrder
      .map(id => page.pageSections[id])
      .filter((block): block is PageSection => !!block);

    const subgroups = this.compareOptions().map(option => {
      const records = applyFilters(
        this.filteredRecords(), { [key]: String(option.value) }, design.variableMap, this.layout());
      return {
        label: option.label,
        size: records.length,
        // Weighted, because it rebases "Not asked" against weighted counts.
        weightedSize: weightedSize(records, this.weightOf()),
        records,
        aggregates: aggregateAll(records, variableKeys, design.variableMap, this.layout(), this.weightOf()),
      };
    });

    // Every column gets the same overall table on purpose: a categorical-distribution block's
    // Total Overall bar stays the whole sample in each column, so what varies across the row is only
    // its Dynamic to Filter bar -- which is what makes the pair worth comparing here.
    const overall = this.overallAggregates();
    const rows = blocks.map(block => {
      // A module that sits out of the breakout keeps the subsection-level series it would have had
      // anyway, in one cell, and the template spans it across the columns.
      if (ignoresCompareBy(block)) {
        return {
          block,
          alignCount: 0,
          spans: true,
          cells: [{
            series: seriesForBlock(block, this.aggregates(), {
              variableMap: design.variableMap,
              layout: this.layout(),
              overallAggregates: overall,
              records: this.filteredRecords(),
              weightOf: this.weightOf(),
              statistics: design.statistics,
            }),
            size: this.baseN(),
          }],
        };
      }

      const cells = subgroups.map(subgroup => ({
        series: seriesForBlock(block, subgroup.aggregates, {
          variableMap: design.variableMap,
          layout: this.layout(),
          contextN: subgroup.weightedSize,
          overallAggregates: overall,
          records: subgroup.records,
          weightOf: this.weightOf(),
          statistics: design.statistics,
        }),
        size: subgroup.size,
      }));
      return {
        block,
        alignCount: Math.max(0, ...cells.map(cell => cell.series[0]?.points.length ?? 0)),
        cells,
        spans: false,
      };
    });

    return { columns: subgroups.map(({ label, size }) => ({ label, size })), rows };
  });

  compareColumns = computed(() => this.compareData().columns);
  compareRows = computed(() => this.compareData().rows);

  /** Set the moment a refresh is asked for, so the button locks before the server has answered. */
  private refreshRequested = signal(false);

  /** A source switch in flight: the deck is about to re-render against different data. */
  switchingSource = signal(false);
  dataSourceName = computed(() => this.report()?.dataSourceName ?? '');
  refreshing = computed(() =>
    this.refreshRequested() || this.report()?.dataStatus === EndClientReportStatus.Ingesting);

  private destroy$ = new Subject<void>();
  private stopPolling$ = new Subject<void>();

  /**
   * Design history is for internal users only, for now. It exposes who edited what and when, and
   * whether a client should see that is its own decision -- so this starts closed rather than open.
   *
   * The API enforces it regardless (all three history endpoints refuse a caller with a client
   * scope); this only decides whether to offer it, since a hidden button is not a guard.
   */
  readonly canSeeHistory: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertify: AlertifyService,
    private authService: AuthService,
    private reportService: EndClientReportService,
    private modalService: BsModalService,
    private heatmapImages: HeatmapImageStore,
  ) {
    // Short-circuited for a share link: there is no signed-in user to ask about, and AuthService
    // reads roles off one. A client is not internal by definition, so the question never arises.
    this.canSeeHistory = this.internal && this.authService.isInternal();
  }

  ngOnInit() {
    fromEvent<MediaQueryListEvent>(window.matchMedia(NARROW_LAYOUT_QUERY), 'change')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.narrowLayout.set(event.matches));

    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    // Usually the route says which report this is. A share link's route carries a deployment id
    // instead, so the viewer sets it directly (NK-399) -- everything it identifies is served by the
    // bundle-backed service that comes with it, which doesn't care what the id says.
    this.reportId = this.reportId || (this.route.snapshot.paramMap.get('reportId') ?? '');
    this.templateId = this.route.snapshot.paramMap.get('templateId') ?? '';
    this.backLink = `/projects/${this.projectId}/end-client-reports`;

    // Same component, same editing, different thing being edited: a template is a design with no
    // survey behind it, so it loads its content from the template endpoint and renders against the
    // shared sample survey instead of a project's data.
    if (this.templateId) {
      // Shows the pictures copied in when the template was saved from a report; takes no uploads.
      this.heatmapImages.setTemplate(this.templateId);
      this.loadTemplate();
      return;
    }

    if (!this.reportId) {
      this.loading.set(false);
      this.loadFailed.set(true);
      return;
    }
    this.heatmapImages.setReport(this.reportId);

    this.reportService.loadDeck(this.reportId).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ report, design, records, layout }) => {
        this.report.set(report);
        this.design.set(design);
        this.records.set(records);
        this.layout.set(layout);
        this.selectedSubSectionId.set(this.navSubSections()[0]?.subSection.id ?? null);
        // Opened from a thumbnail's carousel: start on the page it was showing. Counted from 1.
        const startPage = Number(this.route.snapshot.queryParamMap?.get('page'));
        if (!this.thumbnail && startPage > 1) this.goToDeckPage(startPage - 1);
        this.loading.set(false);
      },
      error: () => {
        this.loadFailed.set(true);
        this.loading.set(false);
      }
    });
  }

  /**
   * A template is edited against the survey it was authored on — its own source project's layout
   * and data. Its variableMap holds that project's data positions, so no other survey resolves it:
   * against anything else every chart renders empty.
   */
  private loadTemplate() {
    forkJoin({
      template: this.reportService.getTemplate(this.templateId),
      design: this.reportService.getTemplateContent(this.templateId),
      records: this.reportService.getTemplateRecords(this.templateId),
      layout: this.reportService.getTemplateSurveyLayout(this.templateId),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ template, design, records, layout }) => {
        this.template.set(template);
        this.design.set(design);
        this.records.set(records);
        this.layout.set(layout);
        this.selectedSubSectionId.set(this.navSubSections()[0]?.subSection.id ?? null);
        this.loading.set(false);
      },
      error: error => {
        this.loading.set(false);

        // No source project means nothing to render this template against, so editing it would
        // show empty charts and invite someone to "fix" a design that isn't broken. Back to the
        // list with the reason, rather than an editor that can't do its job.
        if (error?.error?.reason === 'SourceProjectNotFound') {
          this.alertify.error('Unable to edit that template — its source project could not be found.');
          this.router.navigateByUrl(this.backLink);
          return;
        }

        this.loadFailed.set(true);
      }
    });
  }

  ngOnDestroy() {
    this.showLogo(null);
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Edit with AI is always on the deck, as a window or a pill, wherever it can be used: a report
   * rather than a template, for Navigator's own users, and not while presenting or editing by hand --
   * both write the design, and a window over the edit panel would only say to finish editing first.
   * The history is a popup over everything, so the two cannot both be in use at once.
   */
  chatAvailable = computed(() => this.internal && !this.thumbnail && !this.isTemplate() && !this.presenting()
    && !this.editing());

  /** The pill, or the Edit menu's AI Assisted while it is one: the window opened out. */
  openChat() {
    this.chatMinimized.set(false);
    }

  /**
   * Back to a pill -- and the window forgets where it was dragged, so the next time it is opened it
   * opens in the bottom right, by the pill it came from, rather than wherever it was last left.
   */
  minimizeChat() {
    this.chatMinimized.set(true);
    this.chatPosition.set(null);
  }

  /**
   * The chat window's bar taken hold of. Not from its buttons, which are for pressing. Captured, so
   * the window follows the pointer however fast it moves, even off the bar.
   */
  startChatDrag(event: PointerEvent) {
    const box = this.chatWindow?.nativeElement.getBoundingClientRect();
    if (!box || (event.target as HTMLElement | null)?.closest('button')) return;
    this.chatDrag = { dx: event.clientX - box.left, dy: event.clientY - box.top };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    // No text selected across the deck while it is dragged.
    event.preventDefault();
    }

  /** Kept on screen, with its bar always in reach, so it can never be dragged out of sight. */
  dragChat(event: PointerEvent) {
    const frame = this.chatWindow?.nativeElement;
    if (!this.chatDrag || !frame) return;
    const { width } = frame.getBoundingClientRect();
    const clamp = (value: number, max: number) => Math.min(Math.max(0, value), Math.max(0, max));
    this.chatPosition.set({
      left: clamp(event.clientX - this.chatDrag.dx, window.innerWidth - width),
      top: clamp(event.clientY - this.chatDrag.dy, window.innerHeight - CHAT_BAR_REACH_PX),
    });
  }

  endChatDrag(event: PointerEvent) {
    if (!this.chatDrag) return;
    this.chatDrag = null;
    (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
  }

  /**
   * Actions -> History (a deck), or the template editor's History button: the design's history in a
   * popup over the deck. Templates have one too since NK-450, so a theme update pushed to a template
   * can be undone. A restore reloads the design at the version restored, the way an AI edit does,
   * and closes the popup.
   */
  openHistory() {
    if (!this.canSeeHistory) return;
    const modal = this.modalService.show(ReportHistoryModalComponent, {
      // The medium width: a row is a short line and a Restore button, and the large one ran the
      // lines across most of the screen.
      class: 'modal-md',
      initialState: this.isTemplate()
        ? { templateId: this.templateId, disabled: this.editing() }
        : { reportId: this.reportId, disabled: this.editing() },
    });
    if (!modal.content) return;
    modal.content.onRestored = report => this.onChatEdit(report);
    modal.content.onTemplateRestored = template => this.onTemplateReverted(template);
  }

  /**
   * The module the side panel is editing, as the deck draws it: the same block and series its card
   * was handed. Null once that card is not on screen -- another subsection, another page, or
   * removed -- so the panel never edits something the user cannot see change. A paired
   * explainer's matrix is the exception, below.
   */
  editedBlock = computed<RenderedBlock | null>(() => {
    const id = this.editedBlockId();
    if (!id || !this.editing()) return null;
    if (this.comparing()) {
      const row = this.compareRows().find(candidate => candidate.block.id === id);
      return row ? { block: row.block, series: row.cells[0]?.series ?? [] } : null;
    }
    const shown = this.renderedRows()
      .flatMap(row => [row.full, row.left, row.right])
      .filter((cell): cell is RenderedBlock => !!cell);
    const onPage = shown.find(cell => cell.block.id === id);
    if (onPage) return onPage;
    // The one exception: the matrix a paired explainer reads, opened from the explainer's pen on a
    // page of its own. The explainer redraws from it, so the change is still in view.
    const explainerShown = shown.some(cell => this.contentDerived(cell.block));
    return explainerShown ? this.ladderAsDrawn(id) : null;
  });

  /**
   * What a card's pen opens: its own settings, except on an explainer paired with a matrix. Its
   * stages, names and band are all the matrix's, so the matrix's settings are the ones to change.
   */
  editTargetOf(block: PageSection): string {
    if (!this.contentDerived(block)) return block.id;
    const subSection = this.selectedSubSection();
    return (subSection && this.ladderIn(subSection)?.id) || block.id;
  }

  /** This subsection's matrix, drawn as its own page draws it, for the panel to be handed. */
  private ladderAsDrawn(id: string): RenderedBlock | null {
    const subSection = this.selectedSubSection();
    const design = this.design();
    const ladder = subSection && this.ladderIn(subSection);
    if (!design || !ladder || ladder.id !== id) return null;
    return {
      block: ladder,
      series: seriesForBlock(ladder, this.aggregates(), {
        variableMap: design.variableMap,
        layout: this.layout(),
        overallAggregates: this.overallAggregates(),
        records: this.filteredRecords(),
      }),
    };
  }

  /**
   * Whether the module panel's track is showing: all through edit mode, with or without a module in
   * it, unless the chat or the history has the right-hand side instead.
   */
  moduleRailOpen = computed(() => this.editing() && !this.presenting());

  /**
   * A card clicked, or tabbed into, in edit mode: its settings open in the panel. Clicking it again
   * leaves them open -- typing into a card's title is a click on the card too, and closing the panel
   * under the cursor would be the last thing wanted. The panel's Close is the way out.
   */
  openBlockEditor(blockId: string) {
    if (this.editedBlockId() === blockId) return;
    this.editedBlockId.set(blockId);
    this.movedAway.set(null);
  }

  closeBlockEditor() {
    this.editedBlockId.set(null);
  }

  /**
   * How wide a module's column has to be for the module to be drawn at its own minimum, or null for
   * one with none of its own beyond the subsection's. The module's width plus its card's padding and
   * the column's gutters, so the card grows round it and the deck scrolls sideways, rather than the
   * module spilling out of its card or scrolling inside it.
   */
  columnMinWidth(cell: RenderedBlock): number | null {
    const own = Math.max(
      ladderMinWidth(cell.block, cell.series, false),
      explainerMinWidth(cell.block, false),
    );
    return own ? own + CARD_PADDING_X + COLUMN_GUTTERS_X : null;
  }

  /** What a card's buttons call the module: its title, or failing that the kind of module it is. */
  blockLabel(block: PageSection): string {
    return block.header?.trim() || REPORT_BLOCK_LABELS[block.reportType];
  }

  /**
   * A chat turn changed the design. The report comes back on the response (so the version is
   * already right); the design itself has to be re-read, since the merge happened server-side and
   * this browser never saw it.
   */
  onChatEdit(report: EndClientReport) {
    this.report.set(report);
    this.staleAfterEdit.set(false);
    this.reportService.getReportDesign(this.reportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: design => {
          this.design.set(design);
          // The edit may have deleted or moved the subsection being viewed, which would otherwise leave
          // the deck on an id that no longer resolves.
          if (!this.navSubSections().some(nav => nav.subSection.id === this.selectedSubSectionId())) {
            this.selectedSubSectionId.set(this.navSubSections()[0]?.subSection.id ?? null);
          }
        },
        // The save itself already succeeded, so this is a stale view rather than a lost edit --
        // say exactly that instead of implying the change didn't happen.
        error: () => this.staleAfterEdit.set(true)
      });
  }

  /**
   * A template restored from its history (NK-450): re-read the content the restore just wrote. The
   * template counterpart of `onChatEdit`, which is what a deck's restore goes through.
   */
  onTemplateReverted(template: EndClientReportTemplate) {
    this.template.set(template);
    this.staleAfterEdit.set(false);
    this.reportService.getTemplateContent(template.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: design => {
          this.design.set(design);
          if (!this.navSubSections().some(nav => nav.subSection.id === this.selectedSubSectionId())) {
            this.selectedSubSectionId.set(this.navSubSections()[0]?.subSection.id ?? null);
          }
        },
        error: () => this.staleAfterEdit.set(true)
      });
  }

  /** Opens the picker, loading the project's sources into it once they arrive. */
  chooseDataSource() {
    const report = this.report();
    if (!report || this.refreshing() || this.editing()) return;

    const chooser = this.modalService.show(DataSourceModalComponent, { class: 'modal-md' }).content;
    if (!chooser) return;
    chooser.onConfirm = (dataSourceId: string) => this.switchDataSource(dataSourceId);
    // The dialog loads and reloads its own list, since uploading a source from inside it changes
    // what that list holds.
    chooser.load(report.id, report.projectId, report.dataSourceId);
  }

  private switchDataSource(dataSourceId: string) {
    this.switchingSource.set(true);
    this.reportService.setDataSource(this.reportId, dataSourceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        // Records and datamap both resolve through the source, so neither survives the switch --
        // re-fetching them is what actually re-renders the deck against the new data.
        next: report => {
          this.report.set(report);
          this.reloadRecords();
          this.reloadLayout();
          this.switchingSource.set(false);
        },
        error: () => this.switchingSource.set(false)
      });
  }

  refreshData() {
    const report = this.report();
    const dataSourceId = report?.dataSourceId;
    if (this.refreshing() || !report || !dataSourceId) return;

    const modal = this.modalService.show(RefreshDataModalComponent, { class: 'modal-md' });
    const chooser = modal.content;
    if (!chooser) return;
    chooser.onConfirm = (includeDatamap: boolean) => this.startRefresh(includeDatamap);

    // Who else reads this source. Fetched rather than assumed: a source shared into another project
    // is invisible from here otherwise, and that is exactly who a refresh would surprise.
    chooser.expectImpact(report.id, report.projectId);
    this.reportService.getReportsUsingDataSource(dataSourceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: usage => chooser.setImpact(usage),
        error: () => chooser.impactUnavailable()
      });
  }

  private startRefresh(includeDatamap: boolean) {
    const dataSourceId = this.report()?.dataSourceId;
    if (!dataSourceId) return;

    // Before the request, not after it: the round trip is long enough to press the button again,
    // and each press would start another run.
    this.refreshRequested.set(true);

    this.reportService.refreshData(dataSourceId, includeDatamap)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        // The source comes back Ingesting, which is what keeps the button locked once the flag above
        // is released at the end of the run.
        next: source => {
          this.applyDataState(source.status, source.dataFetchedAt);
          this.pollUntilRefreshed(dataSourceId, includeDatamap);
        },
        error: () => {
          this.refreshRequested.set(false);
          this.applyDataState(undefined, this.report()?.dataFetchedAt);
        }
      });
  }

  // Polls the source's own light status route rather than the report, then reloads the report once
  // it lands: status only has to say "still going, or worth pulling the full thing again?".
  private pollUntilRefreshed(dataSourceId: string, includeDatamap: boolean) {
    interval(REFRESH_POLL_MS).pipe(
      switchMap(() => this.reportService.getDataSourceStatus(dataSourceId)),
      takeUntil(this.stopPolling$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: source => {
        this.applyDataState(source.status, source.dataFetchedAt);
        if (source.status !== EndClientReportStatus.Ingesting) {
          this.stopPolling$.next();
          this.refreshRequested.set(false);
          this.reloadReport();
          this.reloadRecords();
          // A refresh can copy in survey images that a heatmap was told did not exist.
          this.heatmapImages.forget();
          // The datamap is what every variable resolves through, so leaving it as it was would
          // render the new respondents against the old survey -- new questions missing, renamed
          // answers still reading their old wording.
          if (includeDatamap) this.reloadLayout();
        }
      },
      // Released on error too, or a failed poll would leave the button locked for good.
      error: () => {
        this.refreshRequested.set(false);
        this.stopPolling$.next();
      }
    });
  }

  // Data state lives on the report, so a run in flight is reflected there rather than in a signal
  // of its own -- one place the deck reads it from, whether it arrived with the report or from a poll.
  private applyDataState(status: EndClientReportStatus | undefined, dataFetchedAt?: string) {
    const report = this.report();
    if (report) this.report.set({ ...report, dataStatus: status, dataFetchedAt });
  }

  private reloadReport() {
    this.reportService.getReport(this.reportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: report => this.report.set(report) });
  }

  private reloadLayout() {
    this.reportService.getSurveyLayout(this.reportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: layout => this.layout.set(layout) });
  }

  private reloadRecords() {
    this.reportService.getRecords(this.reportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: records => this.records.set(records) });
  }

  dataAsOfLabel(): string {
    const stamp = this.report()?.dataFetchedAt;
    if (!stamp) return '';
    try {
      // parseUtcDate rather than parseISO -- see the note in report-history.component.ts. A
      // date-only label still gets this wrong without it: an evening UTC timestamp reads as the
      // previous day for anyone west of it.
      return `Data as of ${format(parseUtcDate(stamp), 'd MMM yyyy')}`;
    } catch {
      return '';
    }
  }

  selectSubSection(subSectionId: string) {
    this.wantedPage.set(0);
    this.selectedSubSectionId.set(subSectionId);
    this.closeBlockEditor();
    this.movedAway.set(null);
  }

  toggleEditing() {
    // Belt as well as braces. The control that calls this is hidden from a share link, but hiding a
    // button is presentation -- this is the state itself refusing, so a client view cannot end up in
    // edit mode however it was reached.
    if (!this.internal) return;

    if (this.editing()) {
      this.saveName();
      // Edit mode stays on until the design save lands. Dropping out of it first would show the
      // read-only deck as though the edits were stored, and there'd be no way back to the editing
      // chrome if the save turned out to be rejected.
      if (this.dirty()) {
        this.saveDesign();
        return;
      }
      this.editing.set(false);
      this.confirmingSubSectionDelete.set(false);
      this.closeBlockEditor();
      return;
    }
    this.editedName.set(this.deckName());
    this.editedDescription.set(this.deckDescription());
    // Colours or statistics previewed from the top bar become part of the edit rather than being
    // lost: Cancel goes back to before them, and a theme picked there still rides on the save.
    const session = this.settingsSession();
    this.designBeforeEditing = session ? session.before : this.design();
    if (!session) this.pendingTheme.set(null);
    this.settingsSession.set(null);
    this.confirmingCancel.set(false);
    this.editing.set(true);
    this.themePanelOpen.set(false);
    this.loadThemes();
  }

  /**
   * The picker's list. A template names its own client; a deck names its project and the server
   * resolves the client, so a deck can only ever be offered its own client's themes.
   */
  private loadThemes() {
    if (this.themesLoaded) return;
    const clientId = this.template()?.clientId;
    const scope = this.isTemplate()
      ? (clientId ? { clientId } : null)
      : (this.projectId ? { projectId: this.projectId } : null);
    if (!scope) return;

    this.themesLoaded = true;
    this.reportService.getThemes(scope)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: themes => this.themes.set(themes),
        // The picker just stays empty; the dots still work, so editing colours isn't blocked.
        error: () => { this.themesLoaded = false; },
      });
  }

  /**
   * NK-450 — switches the deck onto a theme: its colours over the deck's now, and the theme itself
   * recorded when the edit is saved. An ordinary edit, so Cancel undoes it like any other.
   */
  pickTheme(theme: EndClientReportTheme) {
    this.applyEdit(design => withThemeLogo(applyThemePalette(design, theme.theme.colorPalette), theme.theme.logo));
    this.pendingTheme.set({ themeId: theme.id, themeVersion: theme.themeVersion });
  }

  /** NK-518 — the deck's own "don't show the logo". Kept through theme updates, which only replace the logo. */
  setHideLogo(hidden: boolean) {
    this.applyEdit(design => {
      const { hideLogo: _, ...rest } = design;
      return hidden ? { ...rest, hideLogo: true } : rest;
    });
  }

  uploadingLogo = signal(false);
  logoError = signal<string | null>(null);

  /**
   * NK-518 — this deck's own logo in place of its theme's: an edit like a hand-picked colour, so it
   * previews until saved and Cancel undoes it. Unlike a colour it survives theme updates
   * (`usingCustomLogo`), until "Use theme's logo" takes it back. Stored where theme logos are, since
   * all a design holds is the id.
   */
  uploadReportLogo(input: HTMLInputElement) {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      this.logoError.set('Logos can be up to 2 MB.');
      return;
    }

    this.uploadingLogo.set(true);
    this.logoError.set(null);
    this.reportService.uploadThemeLogo(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: imageId => {
          this.uploadingLogo.set(false);
          this.applyEdit(design => withCustomLogo(design, { imageId }));
        },
        error: () => {
          this.uploadingLogo.set(false);
          this.logoError.set("Couldn't upload that logo. Use a JPEG, PNG, GIF or WebP image up to 2 MB.");
        },
      });
  }

  /** Back to the theme's logo -- or to none, where the theme has none -- leaving the colours alone. */
  useThemeLogo() {
    const theme = this.currentTheme();
    this.applyEdit(design => withoutCustomLogo(design, theme?.theme.logo));
    this.logoError.set(null);
  }

  /** The deck uses a logo uploaded for it, which theme updates leave alone. */
  logoCustomised = computed(() => !!this.design()?.usingCustomLogo);

  /** Drops this deck's own colour and logo changes: its theme again, every colour including the text. */
  revertToTheme() {
    const theme = this.currentTheme();
    if (!theme) return;
    this.applyEdit(design => withoutCustomLogo(design, theme.theme.logo));
    this.pickTheme(theme);
  }

  /** "Update available": the same theme again, at its latest colours. */
  updateToLatestTheme() {
    const theme = this.themes().find(candidate => candidate.id === this.currentThemeId());
    if (theme) this.pickTheme(theme);
  }

  /**
   * Leaves edit mode and puts the deck back the way it was opened -- design, name and description.
   * Asks first where there is something to lose, because there is no undo once it is gone.
   */
  cancelEditing() {
    if (this.savingDesign()) return;
    if (this.unsaved() && !this.confirmingCancel()) {
      this.confirmingCancel.set(true);
      return;
    }

    const before = this.designBeforeEditing;
    if (before) {
      this.design.set(before);
      // The subsection being looked at may have been added during the edit, and is now gone with it.
      if (!this.navSubSections().some(nav => nav.subSection.id === this.selectedSubSectionId())) {
        this.selectedSubSectionId.set(this.navSubSections()[0]?.subSection.id ?? null);
      }
    }

    this.editedName.set(this.deckName());
    this.editedDescription.set(this.deckDescription());
    this.dirty.set(false);
    this.pendingTheme.set(null);
    this.designSaveFailure.set(null);
    this.confirmingCancel.set(false);
    this.confirmingSubSectionDelete.set(false);
    this.editing.set(false);
    this.closeBlockEditor();
  }

  /**
   * Saves the deck's current design as a reusable template for this project's client.
   *
   * Named after the deck and renamed later in the template editor rather than prompting here: this
   * is a full-subsection route in core, and a name prompt would mean a modal that only `main` has. The
   * client is resolved server-side from the project -- the deck knows which project it belongs to
   * and has no business asserting a client id.
   */
  saveAsTemplate() {
    const design = this.design();
    if (!design || this.savingTemplate() || this.isTemplate()) return;

    // The template goes on the deck's theme (NK-450). Only a real theme is passed: asking for OR
    // Default would stamp the default colours over a deck someone has recoloured by hand, where
    // leaving it out keeps the deck's colours exactly and lands on OR Default all the same.
    const themeId = this.report()?.themeId;

    this.savingTemplate.set(true);
    this.reportService.createTemplate({
      name: this.deckName(),
      description: this.deckDescription() || undefined,
      projectId: this.projectId,
      template: this.asTemplateDocument(design),
      sourceReportId: this.reportId,
      theme: themeId ? { themeId } : undefined,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: template => {
        this.savingTemplate.set(false);
        this.templateSaved.set(template.name);
        // A confirmation, so it goes by itself -- unless another save has replaced it since.
        setTimeout(() => {
          if (this.templateSaved() === template.name) this.templateSaved.set(null);
        }, CONFIRMATION_BANNER_MS);
      },
      error: () => {
        this.savingTemplate.set(false);
        this.templateSaveFailed.set(true);
      },
    });
  }

  /**
   * What this deck's template asked for that the survey couldn't provide. Navigator's own chrome,
   * never shown while presenting -- a client should see the deck that exists, not a note about the
   * one that was asked for.
   */
  unresolvedSlots = computed(() => this.design()?.unresolvedSlots ?? []);
  unresolvedOpen = signal(false);

  /**
   * Variables this deck maps that its data doesn't have. Computed from the design and datamap in
   * front of it rather than recorded anywhere, so it is right whatever put the deck in that state --
   * a source switch, a structure refresh that dropped a question, or a design built elsewhere.
   *
   * Empty while the datamap is still loading: an empty layout would otherwise report every variable
   * as missing for a second.
   */
  unresolvedVariables = computed(() => {
    const design = this.design();
    const layout = this.layout();
    if (!design || !layout || Object.keys(layout).length === 0) return [];
    return unresolvedVariables(design, layout);
  });
  unresolvedVariablesOpen = signal(false);

  /**
   * The data notes closed this visit, each by what it listed: a note comes back only when its list
   * changes -- a source switch that loses another question is news, the same list again is not.
   */
  private dismissedNotes = signal<ReadonlySet<string>>(new Set());

  unresolvedVariablesNote = computed(() => 'questions:' + this.unresolvedVariables().join('|'));
  unresolvedSlotsNote = computed(() => 'slots:' + this.unresolvedSlots().map(slot => slot.slot).join('|'));

  isNoteDismissed(note: string): boolean {
    return this.dismissedNotes().has(note);
  }

  dismissNote(note: string) {
    this.dismissedNotes.set(new Set([...this.dismissedNotes(), note]));
  }

  /**
   * Marks one outstanding item as dealt with, and saves straight away. Not folded into the normal
   * edit-then-save flow because it isn't a subsection edit: it's a note being cleared, and it's available
   * whether or not someone is editing.
   *
   * Refused mid-edit for that reason -- saving here writes the whole design, so doing it with subsection
   * edits in flight would commit work the user hasn't chosen to keep yet.
   */
  dismissUnresolved(slot: string) {
    const design = this.design();
    if (!design || this.editing() || this.savingDesign()) return;

    this.design.set(removeUnresolvedSlot(design, slot));
    this.saveDesign();
  }

  /**
   * A design on its way to being stored as a template: the same document plus a description of what
   * each variable reference is looking for, derived from the survey it was bound to.
   *
   * `variableMap` is kept alongside the slots rather than stripped. It's what renders the template
   * in this editor, and applying the template to a study replaces it wholesale — so leaving it costs
   * nothing and removing it would leave the editor with nothing to draw.
   */
  private asTemplateDocument(design: ReportDesign): ReportDesign {
    return templateDocumentFrom(design, this.layout());
  }

  /** Retry after a Busy failure -- the payload is unchanged, the claim has just expired by now. */
  retryDesignSave() {
    this.saveDesign();
  }

  /**
   * Public because the Save changes button calls it: a template can only reach public members.
   * `onSaved` runs once the design has landed; `withName` is off for a save from the Theme or
   * Statistics panel while viewing, where the name fields are not on screen.
   */
  saveDesign(onSaved?: () => void, withName = true) {
    const design = this.design();
    if (!design || this.savingDesign()) return;

    // Save changes is one button, so it commits both halves: the design document here and the
    // name/description, which are a row update of their own. Only the exit path used to do this,
    // so a title typed and then saved from this button was simply dropped.
    if (withName) this.saveName();

    // The document itself is unchanged -- someone edited only the title. Writing it anyway would
    // archive a version and bump the counter for an edit that is not in the document.
    if (!this.dirty()) {
      this.editing.set(false);
      this.confirmingSubSectionDelete.set(false);
      return;
    }

    const template = this.template();
    const report = this.report();
    if (this.isTemplate() ? !template : !report) return;

    this.savingDesign.set(true);
    this.designSaveFailure.set(null);

    // Both endpoints take the same contract -- whole document plus the version it was based on --
    // and fail the same way, so only the call and where the returned version lands differ. A theme
    // picked during the edit rides along, so its columns change in the same write as its colours.
    const theme = this.pendingTheme() ?? undefined;
    const save = this.isTemplate()
      ? this.reportService.saveTemplateContent(template!.id, this.asTemplateDocument(design), this.baseVersion(), theme)
        .pipe(map(saved => () => this.template.set(saved)))
      : this.reportService.saveReportDesign(report!.id, design, this.baseVersion(), theme)
        .pipe(map(saved => () => this.report.set(saved)));

    save.pipe(takeUntil(this.destroy$)).subscribe({
      next: apply => {
        // Carries the version this save landed on, which the next one has to send as its
        // baseVersion -- without this the second save of a session conflicts with the first.
        apply();
        this.dirty.set(false);
        this.pendingTheme.set(null);
        this.savingDesign.set(false);
        this.editing.set(false);
        onSaved?.();
      },
      error: (failure: DesignSaveFailure) => {
        this.designSaveFailure.set(failure);
        this.savingDesign.set(false);
      },
    });
  }

  /**
   * Recovery from a VersionConflict: re-fetch the report and design that are actually in S3. This
   * discards the local edits -- merging two edited copies of a design isn't something we can do
   * safely, so the banner says so before offering this.
   */
  reloadDesign() {
    const reportId = this.reportId;
    if (!reportId || this.savingDesign()) return;

    this.savingDesign.set(true);
    forkJoin({
      report: this.reportService.getReport(reportId),
      design: this.reportService.getReportDesign(reportId),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ report, design }) => {
        this.report.set(report);
        this.design.set(design);
        // The reloaded design may not contain the subsection that was selected.
        if (!this.navSubSections().some(nav => nav.subSection.id === this.selectedSubSectionId())) {
          this.selectedSubSectionId.set(this.navSubSections()[0]?.subSection.id ?? null);
        }
        this.dirty.set(false);
        this.pendingTheme.set(null);
        this.designSaveFailure.set(null);
        this.savingDesign.set(false);
        this.editing.set(false);
      },
      error: () => {
        this.designSaveFailure.set('Unknown');
        this.savingDesign.set(false);
      },
    });
  }

  private saveName() {
    const name = this.editedName().trim();
    const description = this.editedDescription().trim();
    if (!name || name === this.deckName() && description === this.deckDescription()) return;

    // A row update either way, separate from the design save -- which is why one can succeed while
    // the other fails, and why a failure raises a banner rather than reverting what was typed.
    const template = this.template();
    const report = this.report();
    const save = this.isTemplate()
      ? template && this.reportService.updateTemplate(template.id, name, description)
        .pipe(map(updated => () => this.template.set(updated)))
      : report && this.reportService.updateReport(report.id, name, description)
        .pipe(map(updated => () => this.report.set(updated)));
    if (!save) return;

    save.pipe(takeUntil(this.destroy$)).subscribe({
      next: apply => apply(),
      error: () => this.nameSaveFailed.set(true),
    });
  }

  private navIndexOf(subSectionId: string): number {
    return this.navSubSections().findIndex(nav => nav.subSection.id === subSectionId);
  }

  canMove(subSectionId: string, direction: -1 | 1): boolean {
    const design = this.design();
    if (!design) return false;
    const section = design.sectionOrder
      .map(id => design.sections[id])
      .find(candidate => candidate?.pageOrder.includes(subSectionId));
    if (!section) return false;
    const at = section.pageOrder.indexOf(subSectionId);
    const target = at + direction;
    return target >= 0 && target < section.pageOrder.length;
  }

  moveSubSection(subSectionId: string, direction: -1 | 1) {
    this.applyEdit(design => moveSubSection(design, subSectionId, direction));
  }

  /** Alt and an arrow on a subsection's name: one place along, focus kept on the name. */
  moveSubSectionByKey(subSectionId: string, direction: -1 | 1) {
    const design = this.design();
    if (!design) return;
    const name = this.navSubSections().find(nav => nav.subSection.id === subSectionId)?.subSection.name ?? '';

    if (this.canMove(subSectionId, direction)) {
      this.moveSubSection(subSectionId, direction);
      this.moveAnnouncement.set(`Moved ${name} ${direction < 0 ? 'up' : 'down'}.`);
    } else {
      // At the end of its section: over the edge into the next one, at its near end -- the bottom of
      // the section above, or the top of the one below. The keyboard's way into another section, now
      // that there is no dropdown to choose one from.
      const at = design.sectionOrder.findIndex(id => design.sections[id]?.pageOrder.includes(subSectionId));
      const neighbourId = at === -1 ? undefined : design.sectionOrder[at + direction];
      const neighbour = neighbourId ? design.sections[neighbourId] : undefined;
      if (!neighbourId || !neighbour) return;
      const beforeId = direction < 0 ? null : neighbour.pageOrder[0] ?? null;
      this.applyEdit(current => placeSubSection(current, subSectionId, neighbourId, beforeId));
      this.moveAnnouncement.set(`Moved ${name} into ${neighbour.name}.`);
    }
    setTimeout(() => this.subSectionLinkOf(subSectionId)?.focus());
  }

  /** The subsection's name in the index, where focus goes back to after Alt and an arrow moves it. */
  private subSectionLinkOf(subSectionId: string): HTMLElement | undefined {
    const links = this.deckNav?.nativeElement.querySelectorAll<HTMLElement>('[data-subsection-link]') ?? [];
    return Array.from(links).find(link => link.getAttribute('data-subsection-link') === subSectionId);
  }

  canMoveSection(sectionId: string, direction: -1 | 1): boolean {
    const order = this.design()?.sectionOrder ?? [];
    const target = order.indexOf(sectionId) + direction;
    return order.includes(sectionId) && target >= 0 && target < order.length;
  }

  moveSection(sectionId: string, direction: -1 | 1) {
    this.applyEdit(design => moveSection(design, sectionId, direction));
  }

  /** A subsection's grip picked up: the drag carries its name, as a card's carries the card's. */
  /**
   * A subsection's row picked up. Nothing follows the pointer: the row itself fades where it is,
   * and the line shows where it would land -- in a list this short, a copy of the name trailing the
   * cursor only covered the rows being aimed between.
   */
  startSubSectionDrag(event: DragEvent, subSection: ReportSubSection) {
    const transfer = event.dataTransfer;
    if (!transfer || !this.editing()) return;
    transfer.effectAllowed = 'move';
    transfer.setData(SUBSECTION_DRAG_TYPE, subSection.id);
    transfer.setDragImage(NO_DRAG_IMAGE, 0, 0);
    this.subSectionDrag.set(subSection.id);
  }

  endSubSectionDrag() {
    this.subSectionDrag.set(null);
    this.subSectionDrop.set(null);
  }

  /**
   * Over the index while a subsection is dragged: the gap nearest the pointer, read off the whole
   * list so the space between rows counts too. A section's name means the top of that section. In
   * the space between one section's last row and the next section, whichever of the two edges is
   * nearer -- the end of the one above, or the top of the one below.
   *
   * Marks only a place that moves it; anywhere else marks nothing and letting go does nothing.
   */
  onNavDragOver(event: DragEvent) {
    const dragged = this.subSectionDrag();
    const design = this.design();
    const nav = this.deckNav?.nativeElement;
    if (!dragged || !design || !nav) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';

    const firstOf = (sectionId: string) => design.sections[sectionId]?.pageOrder[0] ?? null;
    let drop: { sectionId: string; beforeId: string | null } | null = null;

    const heading = (event.target as HTMLElement | null)?.closest?.<HTMLElement>('[data-nav-heading]');
    if (heading?.dataset['navHeading']) {
      drop = { sectionId: heading.dataset['navHeading'], beforeId: firstOf(heading.dataset['navHeading']) };
    } else {
      const rows = Array.from(nav.querySelectorAll<HTMLElement>('[data-nav-row]'));
      const boxes = rows.map(row => row.getBoundingClientRect());
      const before = boxes.findIndex(box => event.clientY < box.top + box.height / 2);
      const sectionOf = (index: number) => rows[index]?.dataset['navSection'] ?? '';
      if (before === -1) {
        if (rows.length) drop = { sectionId: sectionOf(rows.length - 1), beforeId: null };
      } else {
        const below = { sectionId: sectionOf(before), beforeId: rows[before].dataset['navRow'] ?? null };
        const startsSection = before > 0 && sectionOf(before - 1) !== sectionOf(before);
        const nearerAbove = startsSection
          && event.clientY - boxes[before - 1].bottom < boxes[before].top - event.clientY;
        drop = nearerAbove ? { sectionId: sectionOf(before - 1), beforeId: null } : below;
      }
    }

    const moves = !!drop && placeSubSection(design, dragged, drop.sectionId, drop.beforeId) !== design;
    const next = moves ? drop : null;
    const current = this.subSectionDrop();
    if (current?.sectionId !== next?.sectionId || current?.beforeId !== next?.beforeId) {
      this.subSectionDrop.set(next);
    }
  }

  onNavDrop(event: DragEvent) {
    const dragged = this.subSectionDrag();
    if (!dragged) return;
    event.preventDefault();
    const drop = this.subSectionDrop();
    this.endSubSectionDrag();
    if (!drop) return;
    const name = this.navSubSections().find(nav => nav.subSection.id === dragged)?.subSection.name ?? '';
    const section = this.sections().find(candidate => candidate.id === drop.sectionId)?.name ?? '';
    this.applyEdit(design => placeSubSection(design, dragged, drop.sectionId, drop.beforeId));
    this.moveAnnouncement.set(`Moved ${name} in ${section}.`);
  }

  /** For the index's markers: a line above this subsection, or under the last of this section. */
  subSectionDropsBefore(subSectionId: string): boolean {
    return this.subSectionDrop()?.beforeId === subSectionId;
  }

  subSectionDropsAtEnd(sectionId: string): boolean {
    const drop = this.subSectionDrop();
    return !!drop && drop.sectionId === sectionId && drop.beforeId === null;
  }

  /** Indexes into the list the nav is drawing, as startsSection does. */
  endsSection(index: number): boolean {
    const pages = this.visibleNavSubSections();
    return index === pages.length - 1 || pages[index].sectionId !== pages[index + 1].sectionId;
  }

  /** The five palette roles, in the order they read as a set rather than alphabetically. */
  readonly paletteRoles: { role: keyof ColorPalette; label: string }[] = [
    { role: 'primary', label: 'Primary' },
    { role: 'secondary', label: 'Secondary' },
    { role: 'accent', label: 'Accent' },
    { role: 'positive', label: 'Positive' },
    { role: 'negative', label: 'Negative' },
  ];

  /** The picker's two rows: the chart and brand colours, then the text. */
  readonly paletteGroups: { label: string; roles: { role: keyof ColorPalette; label: string }[] }[] = [
    { label: 'Colours', roles: this.paletteRoles },
    {
      label: 'Text',
      roles: [
        { role: 'darkText', label: 'Dark text' },
        { role: 'lightText', label: 'Light text' },
      ],
    },
  ];

  /** The deck's text colours, its own where it has them and the defaults where it does not. */
  textColors = computed(() => {
    const p = this.palette();
    return {
      darkText: p.darkText ?? DEFAULT_TEXT_COLORS.darkText,
      lightText: p.lightText ?? DEFAULT_TEXT_COLORS.lightText,
    };
  });

  /** What a picker dot shows: the palette's colour, or for the text, the default it falls back to. */
  colourOf(role: keyof ColorPalette): string {
    if (role === 'darkText' || role === 'lightText') return this.textColors()[role];
    return this.palette()[role] ?? '';
  }


  setPaletteColor(role: keyof ColorPalette, color: string) {
    if (!color) return;
    this.applyEdit(design => setPaletteColor(design, role, color));
  }

  private removeSubSectionById(subSectionId: string) {
    const wasSelected = this.selectedSubSectionId() === subSectionId;
    const at = this.navIndexOf(subSectionId);

    this.applyEdit(design => removeSubSection(design, subSectionId));

    if (!wasSelected) return;
    const pages = this.navSubSections();
    this.selectedSubSectionId.set(pages[Math.min(at, pages.length - 1)]?.subSection.id ?? null);
  }

  /** The deck's content column, which scrolls on its own now that the deck is one window high. */
  @ViewChild('deckMain') private deckMain?: ElementRef<HTMLElement>;
  @ViewChild('deckNav') private deckNav?: ElementRef<HTMLElement>;
  @ViewChild('themeToggle') private themeToggle?: ElementRef<HTMLElement>;

  /**
   * Where the report-colours popup sits: pinned to the window under the Theme button in the top bar,
   * right-aligned to it since the button sits at the right of the bar, and kept on screen.
   */
  themePanelPosition = signal<{ top: number; left: number }>({ top: 0, left: 0 });

  toggleThemePanel() {
    if (this.themePanelOpen()) {
      this.themePanelOpen.set(false);
      return;
    }
    this.placeThemePanel();
    this.themePanelOpen.set(true);
  }

  /**
   * The top bar's Theme button. While viewing, the panel opens without edit mode: colours preview
   * on the deck and the panel's own Save or Cancel keeps or drops them.
   */
  openTheme() {
    if (this.themePanelOpen()) {
      this.closeThemePanel();
      return;
    }
    if (!this.editing()) {
      if (!this.beginSettingsSession()) return;
      this.loadThemes();
    }
    this.placeThemePanel();
    this.themePanelOpen.set(true);
  }

  /** The panel's ×, Esc or its button again. While viewing, that is Cancel. */
  closeThemePanel() {
    this.themePanelOpen.set(false);
    if (!this.editing()) this.discardSettings();
  }

  /** The panel's Save, shown only while viewing. */
  saveThemePanel() {
    this.saveSettings(() => this.themePanelOpen.set(false));
  }

  // The button is in the fixed top bar, so only a change of window size can move it out from under
  // the popup.
  @HostListener('window:resize')
  placeThemePanel() {
    const button = this.themeToggle?.nativeElement.getBoundingClientRect();
    if (!button) return;
    const panelWidth = Math.min(THEME_PANEL_WIDTH, window.innerWidth - 16);
    const left = Math.max(8, Math.min(button.right - panelWidth, window.innerWidth - panelWidth - 8));
    this.themePanelPosition.set({ top: button.bottom + 4, left });
  }
  @ViewChild('chatWindow') private chatWindow?: ElementRef<HTMLElement>;

  /**
   * A blank module at the foot of the current page, with its settings open beside it -- it has no
   * question yet, so the settings are the first thing anyone adding one needs. Added from the
   * panel, so the content is scrolled down to it: otherwise it arrives out of sight.
   */
  addChart() {
    const subSectionId = this.selectedSubSectionId();
    if (!subSectionId) return;
    let blockId = '';
    this.applyEdit(design => addBlock(design, subSectionId, () => (blockId = uuidv4()), this.page()));
    if (!blockId) return;
    this.openBlockEditor(blockId);
    // After the new card has rendered.
    setTimeout(() => {
      const main = this.deckMain?.nativeElement;
      main?.scrollTo?.({ top: main.scrollHeight, behavior: 'smooth' });
    });
  }

  /**
   * The index's Add subsection, under the section it adds to. A subsection cannot be configured in
   * place until it exists, and a subsection with no chart renders as nothing -- so it arrives
   * carrying one blank module, ready to be pointed at a question. It opens straight away, with its
   * title selected in the deck, so the first thing typed names it.
   */
  addSubSection(sectionId?: string) {
    const ids: string[] = [];
    this.applyEdit(design => addBlankSubSection(design, () => this.nextId(ids), sectionId));
    this.openNewSubSection(ids[0], () => this.deckMain?.nativeElement
      .querySelector<HTMLInputElement>('.subsection-title input'));
  }

  /**
   * The index's Add section, at its foot. It arrives holding one blank subsection, which opens, and
   * with its own name selected in the index, so the first thing typed names the section.
   */
  addSection() {
    const ids: string[] = [];
    this.applyEdit(design => addSection(design, () => this.nextId(ids)));
    const sectionId = this.navSubSections().find(nav => nav.subSection.id === ids[0])?.sectionId;
    this.openNewSubSection(ids[0], () => Array.from(
      this.deckNav?.nativeElement.querySelectorAll<HTMLElement>('[data-nav-heading]') ?? [])
      .find(heading => heading.getAttribute('data-nav-heading') === sectionId)
      ?.querySelector<HTMLInputElement>('input'), true);
  }

  /** A fresh id, kept, so whatever was just created can be found by the first one it was given. */
  private nextId(ids: string[]): string {
    const id = uuidv4();
    ids.push(id);
    return id;
  }

  /**
   * Shows a subsection just added, and selects the text of whichever name should be typed first. The
   * search is cleared too: a new subsection is called "Untitled subsection", and would be filtered
   * out of an index narrowed to something else.
   */
  private openNewSubSection(
    subSectionId: string | undefined,
    nameInput: () => HTMLInputElement | null | undefined,
    scrollIndexToEnd = false,
  ) {
    if (!subSectionId) return;
    this.navQuery.set('');
    this.selectSubSection(subSectionId);
    // After the index and the deck have drawn it.
    setTimeout(() => {
      // A new section lands at the end of the index, which on a long one is out of sight behind
      // the pinned foot that was just pressed. Scrolled there instead of left to focus to find:
      // focus scrolls only far enough to show the input, which leaves it under the foot.
      if (scrollIndexToEnd) {
        const nav = this.deckNav?.nativeElement;
        nav?.scrollTo?.({ top: nav.scrollHeight, behavior: 'smooth' });
      }
      const input = nameInput();
      input?.focus({ preventScroll: scrollIndexToEnd });
      input?.select();
    });
  }

  renameSection(sectionId: string, name: string) {
    this.applyEdit(design => renameSection(design, sectionId, name));
  }

  renameSubSection(subSectionId: string, name: string) {
    this.applyEdit(design => renameSubSection(design, subSectionId, name));
  }

  setSubSectionKeyTakeaway(subSectionId: string, takeaway: string) {
    this.applyEdit(design => setSubSectionKeyTakeaway(design, subSectionId, takeaway));
  }

  setSubSectionSubheader(subSectionId: string, subheader: string) {
    this.applyEdit(design => setSubSectionSubheader(design, subSectionId, subheader));
  }

  sections = computed(() => {
    const design = this.design();
    if (!design) return [];
    return design.sectionOrder.map(id => design.sections[id]).filter(Boolean);
  });

  deleteSubSection(subSectionId: string) {
    this.confirmingSubSectionDelete.set(false);
    this.removeSubSectionById(subSectionId);
  }

  // ---- one chart ----

  /**
   * `groupBy` used to be special-cased here, to record the data position a generic patch has
   * nowhere to carry. It now travels the same road as the charted question -- `groupByChange` ->
   * `setBlockGroupBy` -- which also clears the panel choice the old question's answers named, and
   * is covered by tests. Two mechanisms for one field is worse than either, so this one is gone.
   */
  patchBlock(blockId: string, patch: BlockPatch) {
    this.applyEdit(design => updateBlock(design, blockId, patch));
  }

  setBlockVariable(blockId: string, variable: PartVariable) {
    this.applyEdit(design => setBlockVariable(design, blockId, variable));
  }

  setBlockGroupBy(blockId: string, variable: PartVariable) {
    this.applyEdit(design => setBlockGroupBy(design, blockId, variable));
  }

  setBlockPriority(blockId: string, variables: PartVariable[]) {
    this.applyEdit(design => setBlockPriority(design, blockId, variables));
  }

  setBlockSubgroups(
    blockId: string,
    change: { subgroups: { variableKey: string; answer: string }[]; variables: PartVariable[] },
  ) {
    this.applyEdit(design =>
      setBlockSubgroups(design, blockId, change.subgroups, change.variables));
  }

  setBlockTableFilter(
    blockId: string,
    change: { tableFilter: { variableKey: string; answer: string }[]; variables: PartVariable[] },
  ) {
    this.applyEdit(design =>
      setBlockTableFilter(design, blockId, change.tableFilter, change.variables));
  }

  setBlockLayer(blockId: string, variable: PartVariable | null) {
    this.applyEdit(design => setBlockLayer(design, blockId, variable));
  }

  setStatistics(change: { statistics: ReportStatistics; variables: PartVariable[] }) {
    this.applyEdit(design => setReportStatistics(design, change.statistics, change.variables));
  }

  setBlockVariables(blockId: string, variables: PartVariable[]) {
    this.applyEdit(design => setBlockVariables(design, blockId, variables));
  }

  setBlockType(blockId: string, reportType: ReportType) {
    this.applyEdit(design => setBlockType(design, blockId, reportType));
  }

  moveBlock(blockId: string, direction: -1 | 1) {
    this.applyEdit(design => moveBlock(design, blockId, direction));
  }

  /**
   * The arrow keys on a card's grip. The rows are drawn by position, so the card that was focused
   * is now drawing whichever module took its place -- focus goes back to the grip of the one moved,
   * once it has been redrawn, so the next press moves it again.
   */
  moveBlockByKey(blockId: string, direction: -1 | 1) {
    const block = this.selectedSubSection()?.pageSections[blockId];
    if (!block) return;
    this.moveBlock(blockId, direction);
    this.moveAnnouncement.set(`Moved ${this.blockLabel(block)} ${direction < 0 ? 'up' : 'down'}.`);
    setTimeout(() => this.gripOf(blockId)?.focus());
  }

  private gripOf(blockId: string): HTMLElement | undefined {
    const grips = this.deckMain?.nativeElement.querySelectorAll<HTMLElement>('[data-grip]') ?? [];
    return Array.from(grips).find(grip => grip.getAttribute('data-grip') === blockId);
  }

  /**
   * A grip picked up. Nothing follows the pointer -- the browser's own picture of a card is a
   * translucent slab the size of the chart, which hides the very rows it is being aimed between.
   * The card fades where it is instead (see isDragged), and the deck marks where it would land.
   */
  startBlockDrag(event: DragEvent, block: PageSection) {
    const transfer = event.dataTransfer;
    if (!transfer) return;
    transfer.effectAllowed = 'move';
    // A type of its own, not text/plain: an input dragged over takes plain text, and would type the
    // module's id into itself. Firefox will not start a drag carrying no data at all.
    transfer.setData(MODULE_DRAG_TYPE, block.id);
    transfer.setDragImage(NO_DRAG_IMAGE, 0, 0);
    this.dragging.set({ blockId: block.id, moveTarget: this.editTargetOf(block) });
  }

  /** The card being dragged, faded where it sits. */
  isDragged(blockId: string): boolean {
    return this.dragging()?.blockId === blockId;
  }

  /** Every end of a drag: dropped, dropped somewhere it cannot go, or let go of with Escape. */
  endBlockDrag() {
    this.dragging.set(null);
    this.drop.set(null);
    this.dropSubSectionId.set(null);
  }

  /**
   * Over the page: where letting go would put the card. Over the middle of a row, the half of it
   * under the pointer -- which a dragged half can go into where it is empty, or swap into on its own
   * row. Anywhere else, the gap between rows nearest the pointer, measured against the middle of each
   * row. Read off the page as a whole rather than off each row, so the gutters between cards are
   * targets too and the marker does not flicker off as the pointer crosses them.
   *
   * Only what dropBlock says would land as shown is marked; anywhere else marks nothing, and letting
   * go there does nothing. Not in a breakout, where every row is one module drawn once per subgroup
   * and moving them is the arrow keys' job.
   */
  onDeckDragOver(event: DragEvent) {
    const drag = this.dragging();
    const design = this.design();
    if (!drag || !design || this.comparing()) return;
    const rows = Array.from(this.deckMain?.nativeElement.querySelectorAll<HTMLElement>(':scope > .deck-row') ?? []);
    if (!rows.length) return;

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    const boxes = rows.map(row => row.getBoundingClientRect());

    // Over the body of another card -- clear of its top and bottom edges, which are for the gaps --
    // the two would swap, each taking the other's place and size.
    const card = (event.target as HTMLElement | null)?.closest?.<HTMLElement>('[data-drop-block]');
    const targetId = card?.getAttribute('data-drop-block');
    // Anywhere over the card being dragged is "leave it where it is": nothing marked, and letting go
    // puts it back. Without this the card's own edges counted as the gaps beside it, and for a half
    // the gap above is a real move -- out of its pair -- so changing one's mind kept landing there.
    if (targetId && targetId === drag.blockId) {
      this.setDrop(null);
      return;
    }
    if (card && targetId) {
      const cardBox = card.getBoundingClientRect();
      const edge = Math.min(CARD_EDGE_PX, cardBox.height / 4);
      if (event.clientY > cardBox.top + edge && event.clientY < cardBox.bottom - edge) {
        const onto: BlockDrop = { kind: 'onto', targetId };
        if (dropBlock(design, drag.blockId, onto)) {
          this.setDrop(onto);
          return;
        }
      }
    }

    const over = boxes.findIndex(box => event.clientY >= box.top && event.clientY <= box.bottom);
    const box = boxes[over];
    if (box && Math.abs(event.clientY - (box.top + box.height / 2)) < box.height * 0.3) {
      const side: BlockDrop = {
        kind: 'side',
        row: over,
        side: event.clientX < box.left + box.width / 2 ? 'left' : 'right',
      };
      if (dropBlock(design, drag.blockId, side)) {
        this.setDrop(side);
        return;
      }
    }

    const before = boxes.findIndex(row => event.clientY < row.top + row.height / 2);
    const gap: BlockDrop = { kind: 'gap', position: before === -1 ? rows.length : before };
    this.setDrop(dropBlock(design, drag.blockId, gap) ? gap : null);
  }

  /** Set only when it changes, since the pointer reports in many times a second. */
  private setDrop(drop: BlockDrop | null) {
    const current = this.drop();
    const same = current === drop || (!!current && !!drop && JSON.stringify(current) === JSON.stringify(drop));
    if (!same) this.drop.set(drop);
  }

  onDeckDrop(event: DragEvent) {
    event.preventDefault();
    const drag = this.dragging();
    const drop = this.drop();
    this.endBlockDrag();
    const design = this.design();
    if (!drag || !drop || !design) return;
    const dropped = dropBlock(design, drag.blockId, drop);
    if (dropped) this.applyEdit(() => dropped);
  }

  /** For the row markers: whether the drop being shown is this gap, or this half of this row. */
  dropsAtGap(position: number): boolean {
    const drop = this.drop();
    return drop?.kind === 'gap' && drop.position === position;
  }

  dropsAtSide(row: number, side: 'left' | 'right'): boolean {
    const drop = this.drop();
    return drop?.kind === 'side' && drop.row === row && drop.side === side;
  }

  /** Whether letting go would swap the dragged card with this one. */
  dropsOnto(blockId: string): boolean {
    const drop = this.drop();
    return drop?.kind === 'onto' && drop.targetId === blockId;
  }

  /**
   * The left and right arrow keys on a half's grip: to that side of its row, swapping with its
   * partner if it has one. Nothing where that would not draw as a move across -- see dropBlock.
   */
  moveBlockToSide(blockId: string, side: 'left' | 'right') {
    const design = this.design();
    const block = this.selectedSubSection()?.pageSections[blockId];
    if (!design || !block) return;
    const moved = dropBlock(design, blockId, { kind: 'side', row: this.blockIndexOf(blockId), side });
    if (!moved) return;
    this.applyEdit(() => moved);
    this.moveAnnouncement.set(`Moved ${this.blockLabel(block)} to the ${side}.`);
    setTimeout(() => this.gripOf(blockId)?.focus());
  }

  /** The panel's Width. A half's side is wherever it is put afterwards, not something asked here. */
  setBlockWidth(blockId: string, width: BlockWidth) {
    this.applyEdit(design => setBlockWidth(design, blockId, width));
  }

  /** Whether the module being dragged can be dropped on this subsection in the index. */
  canDropOn(subSectionId: string): boolean {
    const drag = this.dragging();
    const design = this.design();
    return !!drag && !!design && canMoveBlockTo(design, drag.moveTarget, subSectionId);
  }

  onSubSectionDragOver(event: DragEvent, subSectionId: string) {
    if (!this.canDropOn(subSectionId)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.dropSubSectionId.set(subSectionId);
  }

  onSubSectionDragLeave(subSectionId: string) {
    if (this.dropSubSectionId() === subSectionId) this.dropSubSectionId.set(null);
  }

  onSubSectionDrop(event: DragEvent, subSectionId: string) {
    event.preventDefault();
    const drag = this.dragging();
    this.endBlockDrag();
    if (drag) this.moveBlockToSubSection(drag.moveTarget, subSectionId);
  }

  /**
   * Into another subsection, from a drop on the index or from the panel's Subsection setting. This
   * page stays on screen, with a note in the panel saying where the module went and a way to follow
   * it -- so several can be sent off in a row without being taken away each time.
   */
  moveBlockToSubSection(blockId: string, subSectionId: string) {
    const design = this.design();
    const block = this.selectedSubSection()?.pageSections[blockId];
    const target = this.navSubSections().find(nav => nav.subSection.id === subSectionId)?.subSection;
    if (!design || !block || !target || !canMoveBlockTo(design, blockId, subSectionId)) return;

    const label = this.blockLabel(block);
    const pages = this.pages();
    const at = pages.indexOf(this.page());
    this.applyEdit(current => moveBlockToSubSection(current, blockId, subSectionId));
    this.closeBlockEditor();
    // As removing does: a page emptied by the move is gone, so show the one before it.
    if (at > 0 && !this.pages().includes(pages[at])) this.wantedPage.set(pages[at - 1]);
    this.movedAway.set({ blockId, subSectionId, label, subSectionName: target.name });
    this.moveAnnouncement.set(`Moved ${label} to ${target.name}.`);
  }

  /** The note's "Go there": the subsection the module went to, on its page, with its settings open. */
  goToMovedBlock() {
    const moved = this.movedAway();
    if (!moved) return;
    const block = this.navSubSections()
      .find(nav => nav.subSection.id === moved.subSectionId)?.subSection.pageSections[moved.blockId];
    this.selectSubSection(moved.subSectionId);
    if (!block) return;
    this.wantedPage.set(pageOf(block));
    this.openBlockEditor(moved.blockId);
  }

  removeBlock(blockId: string) {
    const pages = this.pages();
    const at = pages.indexOf(this.page());
    this.applyEdit(design => removeBlock(design, blockId));
    if (this.editedBlockId() === blockId) this.closeBlockEditor();
    // Taking the last module off a page takes the page with it. Go to the page before it rather than
    // back to the first -- unless it was the first, when the page after it is the new first anyway.
    if (at > 0 && !this.pages().includes(pages[at])) this.wantedPage.set(pages[at - 1]);
  }

  /**
   * The rows of the page being looked at, as block ids, so the editor can grey out the moves it
   * cannot make.
   *
   * Rows and not entries, matching `moveBlock`: the two halves of a left/right pair share a row, so
   * counting entries put the right-hand half at "index 1" and left its up arrow enabled on the top
   * row. One page at a time for the same reason -- the order holds every page's blocks, so a flat
   * count offered a down arrow on the last row of page 1.
   */
  private pageRows(): string[][] {
    const subSection = this.selectedSubSection();
    if (!subSection) return [];
    return layoutRows(subSection, this.page())
      .map(row => [row.full, row.left, row.right].filter(Boolean).map(block => block!.id));
  }

  blockIndexOf(blockId: string): number {
    return this.pageRows().findIndex(row => row.includes(blockId));
  }

  blockCount(): number {
    return this.pageRows().length;
  }

  private applyEdit(change: (design: ReportDesign) => ReportDesign) {
    const design = this.design();
    if (!design) return;
    this.design.set(change(design));
    this.dirty.set(true);
    this.confirmingCancel.set(false);
  }

  setFilter(key: string, value: string) {
    this.activeFilters.update(current => ({ ...current, [key]: value }));
  }

  clearFilter(key: string) {
    this.setFilter(key, '');
  }

  variableName(key: string): string {
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  setCompareBy(key: string) {
    this.compareBy.set(key);
  }

  resetFilters() {
    this.activeFilters.set({});
  }

  togglePresent() {
    // Presenting hides the panel, so a preview left open would have no Save or Cancel to reach.
    if (this.themePanelOpen() && !this.editing()) this.closeThemePanel();
    this.presenting.update(current => !current);
  }

  /**
   * The share link for this deck (NK-399). Opened from the deck rather than the list because what
   * gets published is this report as it stands -- and the dialog says so, since a published deck
   * does not follow later edits until it is re-published.
   */
  share() {
    const modal = this.modalService.show(ShareReportModalComponent, {
      class: 'modal-dialog-centered',
      ignoreBackdropClick: true,
    });

    const dialog = modal.content as ShareReportModalComponent;
    dialog.reportId = this.reportId;
    // The same list the in-deck banner draws. Handed over rather than re-derived server-side, and
    // sent with the publish so the server can refuse if its own answer differs.
    dialog.unresolved = this.unresolvedVariables();
    // So it can say whether the published copy has fallen behind: both are on the report already.
    dialog.designVersion = this.report()?.reportDesignVersion ?? 0;
    dialog.dataFetchedAt = this.report()?.dataFetchedAt ?? null;
    dialog.load();
  }

  @HostListener('document:keydown.escape')
  exitPresent() {
    if (this.presenting()) this.presenting.set(false);
  }

  /** The page controller, and the arrow keys while presenting. Reports whether it moved. */
  nextPage(): boolean {
    const pages = this.pages();
    const at = this.pageIndex();
    if (at >= pages.length - 1) return false;
    this.wantedPage.set(pages[at + 1]);
    return true;
  }

  previousPage(): boolean {
    const pages = this.pages();
    const at = this.pageIndex();
    if (at <= 0) return false;
    this.wantedPage.set(pages[at - 1]);
    return true;
  }

  /**
   * Presenting steps pages first and rolls into the next subsection off the end of the last one, so
   * the arrows walk the deck the way a reader does rather than skipping whatever is on page 2.
   */
  @HostListener('document:keydown.arrowright')
  nextSubSection() {
    if (!this.presenting()) return;
    if (this.nextPage()) return;
    const next = this.navSubSections()[this.subSectionIndex() + 1];
    if (!next) return;
    this.selectedSubSectionId.set(next.subSection.id);
    this.wantedPage.set(0);
  }

  @HostListener('document:keydown.arrowleft')
  previousSubSection() {
    if (!this.presenting()) return;
    if (this.previousPage()) return;
    const previous = this.navSubSections()[this.subSectionIndex() - 1];
    if (!previous) return;
    this.selectedSubSectionId.set(previous.subSection.id);
    // Onto its last page, so stepping back and then forward across the join lands where it left.
    this.wantedPage.set(pagesOf(previous.subSection).slice(-1)[0]);
  }

  /** The ladder in a subsection, if it has one: an explainer there is the front half of it. */
  private ladderIn(subSection: ReportSubSection): PageSection | undefined {
    return subSection.pageSectionOrder
      .map(id => subSection.pageSections[id])
      .find(block => block?.reportType === 'stage-matrix');
  }

  /**
   * A block as it should be drawn. An explainer paired with a ladder takes its panels and its band
   * from that ladder, so the two halves of one idea cannot disagree; its own stored settings survive
   * only as the bullet points, which the ladder has nowhere to hold.
   *
   * Derived here rather than inside the explainer, because this is the level that can see both
   * blocks -- and derived on render rather than written into the document, so renaming a stage on
   * the ladder does not have to remember to rewrite a second block.
   */
  private asDrawn(
    block: PageSection,
    ladder: PageSection | undefined,
    drawnRungs: readonly string[],
  ): PageSection {
    if (block.reportType !== 'stage-explainer') return block;
    if (!ladder || ladder.reportType !== 'stage-matrix') return block;
    return {
      ...block,
      reportExtraData: explainerFromLadder(ladder.reportExtraData, block.reportExtraData, drawnRungs),
    };
  }

  /** For a ladder: where its explainer sits, for the dropdown in its settings. */
  explainerPlacementOf(block: PageSection): ExplainerPlacement {
    const design = this.design();
    if (!design || block.reportType !== 'stage-matrix') return 'none';
    return explainerPlacement(design, block.id);
  }

  /** For an explainer: whether its headings come from a ladder, and so are not edited on it. */
  contentDerived(block: PageSection): boolean {
    if (block.reportType !== 'stage-explainer') return false;
    const subSection = this.selectedSubSection();
    return !!subSection && !!this.ladderIn(subSection);
  }

  setExplainerPlacement(ladderBlockId: string, placement: ExplainerPlacement) {
    this.applyEdit(design => setLadderExplainer(design, ladderBlockId, placement, uuidv4));
  }

  /** Indexes into the list the nav is actually drawing, which search may have narrowed. */
  startsSection(index: number): boolean {
    const pages = this.visibleNavSubSections();
    return index === 0 || pages[index].sectionId !== pages[index - 1].sectionId;
  }
}
